import { AiSupportCard } from 'src/components/shared/aiAssistant/AiSupportCard';
import { marked } from 'marked';
import { createPortal } from 'react-dom';
import React, {
    ChangeEvent,
    ClipboardEvent,
    DragEvent,
    FC,
    KeyboardEvent,
    MouseEvent as ReactMouseEvent,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AiApprovalBanner,
    ChatBubble,
    ChatContainer,
    ChatInput,
    ChatInputArea,
    useSpeechRecognition,
    VoiceInputButton
} from 'src/components/shared/aiAssistant';
import { useSideImagesNoCreditsDialog } from 'src/components/pages/productConfiguration/dialogs/sideImagesNoCreditsDialog';
import { AiOutlineButton, AiPromptSendButton } from 'src/components/widgets/ai';
import { BasicButton, PrimaryButton } from 'src/components/widgets/buttons';
import theme from 'src/shared/theme';
import { Icon } from 'src/components/widgets/icon';
import { Tab, TabList, TabPanel, ZTabs } from 'src/components/widgets/ztabs';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { QuestionDialog, useDialogManager } from 'src/components/widgets/dialog';
import AttachFileIcon from 'src/shared/material_icons/editor/attach_file.svg';
import RestartIcon from 'src/shared/material_icons/device/restart_alt.svg';
import OpenInNewIcon from 'src/shared/material_icons/action/open_in_new.svg';
import SendIcon from 'src/shared/material_icons/content/send.svg';
import StopIcon from 'src/shared/material_icons/av/stop_circle.svg';
import ChatBubbleOutlineIcon from 'src/shared/material_icons/communication/chat_bubble_outline.svg';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import { backofficeUrl, stringFormat, T, uploadFile } from 'src/shared/helpers';
import { useRelayStore } from 'src/relay/environment';
import config from 'src/shared/config';
import styled from 'styled-components';
import { useGenerationPreflight } from 'src/components/pages/aiStudio/hooks/useGenerationPreflight';
import { AgentGestureCard } from './AgentGestureCard';
import { AgentOperationActionCard } from './AgentOperationActionCard';
import { AgentAddonActionCard } from './AgentAddonActionCard';
import { AgentPricingUpgradeCard } from './AgentPricingUpgradeCard';
import {
    AgentArtifactKind,
    AgentArtifactSkeleton,
    AgentTaskPreview,
    ARTIFACT_INTRODUCTIONS,
    artifactIntroduction,
    getAgentArtifacts,
    hasUsefulAgentPreview
} from './AgentTaskPreview';
import {
    AgentChatEntry,
    AgentDraft,
    AgentDraftDocument,
    AgentTurn,
    AgentGeneratedImage,
    AgentGesture,
    LocalAgentAttachment,
    AgentAnswerRecap,
    AgentQuestionnaire,
    AgentGuide
} from './types';
import { AgentQuestionWizard } from './AgentQuestionWizard';
import { followsWidgetAnswers, hasQuestionnaireChoices } from './agentInteractionState';
import { AgentActivityIndicator, AgentAnswerRecapCard, AgentResultRecapCard } from './AgentCommunicationCards';
import { HelpArticleBody } from 'src/components/pages/products/productSidebar/sections/helpArticlePanel';
import { messageForOutcome, useAgentSession } from './useAgentSession';
import { useAgentAssistant } from './AgentAssistantContext';
import { AgentConversationHistory } from './AgentConversationHistory';
import { AgentDraftResultCard } from './AgentDraftResultCard';
import { buildResultRail } from './agentResultRail';

const MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;
const CENTERED_COLUMN_WIDTH_PX = 850;
// The artifact each agent activity delivers in the same reply (see activityLabel in AgentCommunicationCards). General rule:
// while one of these creates an artifact that does not exist yet, the artifact column shows its skeleton.
// Only activities whose result the artifact always shows are listed: variants, sides and scale also prepare a product while the
// agent is still going to ask how to get its images, and a skeleton there would fall back to the conversation.
const ARTIFACT_KIND_BY_ACTIVITY: Record<string, AgentArtifactKind> = {
    generate_images: 'product',
    check_images: 'product',
    detect_areas: 'product',
    edit_areas: 'product',
    edit_printing_method: 'printingMethod',
    edit_pricing: 'pricing'
};

// Blue banner above an artifact being created: what the entity is, in a sentence. {0} is the entity name, the only bold text.
const ArtifactIntroBanner = styled(NotificationBanner)`
    flex-shrink: 0;
    margin-bottom: 12px;
`;

// The artifact an approval applies: its kind, or else its description, names the entity. "printing" and not "print",
// so a product's print areas are not taken for a printing method.
const approvalArtifactKind = (approval: { kind?: string | null; description?: string | null }): AgentArtifactKind | null => {
    for (const text of [approval.kind, approval.description]) {
        const value = text?.toLowerCase() ?? '';
        if (value.includes('printing')) return 'printingMethod';
        if (value.includes('pricing') || value.includes('price')) return 'pricing';
        if (value.includes('product')) return 'product';
    }
    return null;
};

// The artifact an activity or result works on, created or revised: the artifact column follows it.
const WORKED_ARTIFACT_BY_CODE: Record<string, AgentArtifactKind> = {
    generate_images: 'product',
    check_images: 'product',
    detect_areas: 'product',
    edit_areas: 'product',
    set_scale: 'product',
    edit_variants: 'product',
    edit_sides: 'product',
    apply_product: 'product',
    edit_printing_method: 'printingMethod',
    apply_printing_method: 'printingMethod',
    edit_pricing: 'pricing',
    apply_pricing: 'pricing'
};

const APPLY_CODE_ARTIFACTS: Record<string, AgentArtifactKind> = {
    apply_product: 'product',
    apply_printing_method: 'printingMethod',
    apply_pricing: 'pricing'
};

/** Whether this turn applied an approved plan to the catalog. */
const turnAppliedChanges = (turn: AgentTurn) =>
    Boolean(
        turn.hasExternalChanges ||
            turn.resultRecap?.state === 'applied' ||
            turn.resultRecap?.items.some((item) => item.code.startsWith('apply_') && item.state === 'applied')
    );

const requiredAddonsForTurn = (turn: AgentTurn): string[] => {
    const keys = new Set(turn.requiredAddons ?? []);
    // Older AgentService deployments expose these blocks through legacy fields.
    if (turn.requiredPlanFeatures?.includes('large_files')) keys.add('LARGE_FILES');
    for (const image of turn.generatedImages ?? []) {
        if (image.errorCode === 'addon_not_active') keys.add(image.addonKey || 'AI_STUDIO');
    }
    return [...keys];
};

const appliedArtifactKind = (turn: AgentTurn): AgentArtifactKind | null =>
    turn.resultRecap?.items.map((item) => (item.state === 'applied' ? APPLY_CODE_ARTIFACTS[item.code] : undefined)).find(Boolean) ?? null;

// What the agent is doing while it applies an approved plan, rotated on the thinking line like the other AI drawers.
const APPLYING_PLAN_PHRASES: Record<AgentArtifactKind | 'unknown', string[]> = {
    product: [
        T._('Applying the approved plan…', 'Admin'),
        T._('Creating your customizable product…', 'Admin'),
        T._('Saving sides, print areas and variants…', 'Admin'),
        T._('Adding the product to your catalog…', 'Admin')
    ],
    printingMethod: [T._('Applying the approved plan…', 'Admin'), T._('Creating the printing method…', 'Admin'), T._('Saving its settings…', 'Admin')],
    pricing: [T._('Applying the approved plan…', 'Admin'), T._('Creating the pricing rules…', 'Admin'), T._('Saving prices and conditions…', 'Admin')],
    unknown: [T._('Applying the approved plan…', 'Admin'), T._('Saving the changes…', 'Admin'), T._('Almost done…', 'Admin')]
};

const EntityLinks = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`;

// The product ID is not part of the product draft: the confirmation reply states it, as a link to the product
// ("/products?sidebar=1622993", "?product_id=1622993", "/test-mode/1622993") or in the text ("ID **1622993**", "#1622993").
const CREATED_PRODUCT_ID_PATTERNS = [/[?&](?:product_id|sidebar)=(\d+)/i, /\/test-mode\/(\d+)/i, /\bID\W{0,4}(\d{3,})/i, /#(\d{3,})\b/];
const createdProductId = (draft: AgentDraft, message: string | null): number | null => {
    const product = draft.document?.product as { productId?: number; modelId?: number; modelID?: number } | undefined;
    const fromDraft = product?.productId ?? product?.modelId ?? product?.modelID;
    if (fromDraft) return fromDraft;
    for (const pattern of CREATED_PRODUCT_ID_PATTERNS) {
        const match = message?.match(pattern);
        if (match) return Number(match[1]);
    }
    return null;
};

/** Whether this turn created a product: its recap says so, or, without a recap, the reply names the new product. */
const turnCreatedProduct = (turn: AgentTurn, draft: AgentDraft) => {
    if (!turnAppliedChanges(turn)) return false;
    const appliedCodes = (turn.resultRecap?.items ?? []).filter((item) => item.state === 'applied').map((item) => item.code);
    return appliedCodes.length > 0 ? appliedCodes.includes('apply_product') : createdProductId(draft, turn.message) !== null;
};

/** Ways into the entities a confirmation reply created: the product and its test mode, the printing method, the pricing rules. */
const AgentEntityLinks: FC<{ turn: AgentTurn; draft: AgentDraft }> = ({ turn, draft }) => {
    const navigate = useNavigate();
    const { close } = useAgentAssistant();
    const appliedKinds = (turn.resultRecap?.items ?? [])
        .filter((item) => item.state === 'applied')
        .map((item) => APPLY_CODE_ARTIFACTS[item.code])
        .filter(Boolean);
    // Without a recap saying what was applied, every created entity of the draft is offered.
    const includes = (kind: AgentArtifactKind) => appliedKinds.length === 0 || appliedKinds.includes(kind);
    const productId = includes('product') ? createdProductId(draft, turn.message) : null;
    const printingMethodId = includes('printingMethod') ? draft.document?.printingMethodSetup?.printingMethod?.printingMethodId : undefined;
    const pricingGroupId = includes('pricing') ? draft.document?.pricingSetup?.pricingGroup?.pricingGroupId : undefined;
    if (!productId && !printingMethodId && !pricingGroupId) return null;

    const open = (url: string) => {
        close();
        navigate(backofficeUrl(url));
    };

    return (
        <EntityLinks>
            {productId && (
                <>
                    <AiOutlineButton type='button' $size='small' onClick={() => open(`/products?sidebar=${productId}`)}>
                        {T._('Open product', 'Admin')}
                    </AiOutlineButton>
                    <AiOutlineButton type='button' $size='small' onClick={() => open(`/test-mode/${productId}`)}>
                        {T._('Open in test mode', 'Admin')}
                    </AiOutlineButton>
                </>
            )}
            {printingMethodId && (
                <AiOutlineButton type='button' $size='small' onClick={() => open(`/printing-method/${printingMethodId}`)}>
                    {T._('Open printing method', 'Admin')}
                </AiOutlineButton>
            )}
            {pricingGroupId && (
                <AiOutlineButton type='button' $size='small' onClick={() => open(`/pricing-rule?pricing_id=${pricingGroupId}`)}>
                    {T._('Open pricing rules', 'Admin')}
                </AiOutlineButton>
            )}
        </EntityLinks>
    );
};

// The confirm action names what it creates: one approval covers one entity, never everything shown in the artifacts.
const confirmApprovalLabel = (approval: { kind?: string | null; description?: string | null }, isUpdate = false) => {
    if (isUpdate) return T._('Apply update', 'Admin');
    const kind = approvalArtifactKind(approval);
    if (kind === 'product') return T._('Create product', 'Admin');
    if (kind === 'printingMethod') return T._('Create printing method', 'Admin');
    if (kind === 'pricing') return T._('Create pricing rules', 'Admin');
    return T._('Confirm changes', 'Admin');
};

// The closing paragraph of a confirmation that lists optional next steps: it moves to its own message, as chips.
const NEXT_STEPS_PARAGRAPH =
    /prossim[io] pass|next steps?|facoltativ|optional|you can (also |now )?(configure|set up|add|publish)|puoi (anche |ora )?(configurare|impostare|aggiungere|pubblicare)/i;

const withoutNextSteps = (message: string) => {
    // Same normalization as AgentMessageText: escaped newlines become real ones.
    const paragraphs = message.replace(/\\n/g, '\n').split(/\n\s*\n/);
    return paragraphs.length > 1 && NEXT_STEPS_PARAGRAPH.test(paragraphs[paragraphs.length - 1]) ? paragraphs.slice(0, -1).join('\n\n') : message;
};

// The approval banner already offers to apply the plan, so a trailing "shall I apply this to the catalogue?" line is redundant.
const withoutApprovalQuestion = (message: string) => {
    const normalized = message.replace(/\\n/g, '\n');
    const stripped = normalized.replace(/(^|[.!?\n])\s*[^.!?\n]*\b(appl(y|ica)|conferm|confirm|catalog|proceed|procede|salv|save it)[^.!?\n]*\?\s*$/i, '$1');
    return stripped.trim();
};

const CONVERSATION_HISTORY_ID = 'agent-conversation-history';

interface UploadingAttachment extends LocalAgentAttachment {
    status: 'uploading' | 'ready' | 'failed';
    error?: string;
}

// The artifact layout adds a right preview column; the conversation rail is a left overlay so the conversation stays page-centered.
const Root = styled.div`
    display: flex;
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
    background: white;
`;

// The conversation rail floats over the left margin so the conversation itself stays centered on the page.
const RailOverlay = styled.div`
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 3;
    width: 320px;
    flex-direction: column;

    @container (min-width: 900px) {
        display: flex;
    }
`;

// One card per created entity preview (product, printing method, pricing): clicking recalls that preview.
// The whole rail is reserved full-height, with the artifact cards stacked at the top.
const RailEntities = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    overflow-y: auto;
    gap: 6px;
    padding: 12px;
    border-right: 1px solid #e5e7eb;
    background: white;
`;

const RailEntitiesTitle = styled.span`
    flex-shrink: 0;
    color: #90a4ae;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const RailEntityCard = styled.button<{ $active: boolean }>`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid ${(props) => (props.$active ? theme.ai.solid : theme.app.borderColor)};
    border-radius: 8px;
    background: ${(props) => (props.$active ? '#f5f3ff' : 'white')};
    color: ${(props) => (props.$active ? theme.ai.solid : theme.app.textColor)};
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;

    &:hover {
        border-color: ${theme.ai.solid};
    }
`;

const RailEntityLabel = styled.span`
    overflow: hidden;
    min-width: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// The second row of the card: the status badge and, next to it, the revision it is at.
const RailEntityMeta = styled.span`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
`;

// The output type as a small title above the name (e.g. "Customizable Product").
const RailEntityType = styled.span`
    color: #90a4ae;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

// The state of a revision: a pending "Draft", the "Created" one that reached the catalogue, or a superseded "Not used".
type RailBadgeStatus = 'draft' | 'created' | 'unused';
const RAIL_BADGE_COLORS: Record<RailBadgeStatus, { background: string; color: string }> = {
    draft: { background: '#fff3e0', color: '#b26a00' },
    created: { background: '#e6f4ea', color: '#1e7e34' },
    unused: { background: '#eceff1', color: '#90a4ae' }
};
const RailEntityBadge = styled.span<{ $status: RailBadgeStatus }>`
    flex: none;
    padding: 1px 7px;
    border-radius: 999px;
    background: ${(props) => RAIL_BADGE_COLORS[props.$status].background};
    color: ${(props) => RAIL_BADGE_COLORS[props.$status].color};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
`;

// The current revision of the draft, shown on the card the same as in the preview header.
const RailRevPill = styled.span`
    flex: none;
    padding: 1px 7px;
    border-radius: 999px;
    background: #eceff1;
    color: #78909c;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
`;

const DRAWER_TAB_CONVERSATION = 0;

// Drawer only, once there are previews. Narrow, Conversation / Outputs tabs over the whole column. Widened to the
// breakpoint where the rail shows (see RailOverlay), it lays out like full screen: the outputs rail on the left, the
// conversation beside it, and no tabs.
const DrawerTabs = styled(ZTabs)<{ $withRail: boolean }>`
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    flex-direction: column;

    @container (min-width: 900px) {
        ${(props) => props.$withRail && 'padding-left: 320px;'}
    }
`;

// As wide as the drawer, each tab taking half of it.
const DrawerTabList = styled(TabList)`
    flex: none;
    display: flex;
    width: auto;
    margin: 12px 16px 0;

    > li {
        flex: 1;
        justify-content: center;
    }

    @container (min-width: 900px) {
        display: none;
    }
`;

// The selected panel fills the rest of the column, so the conversation keeps its own scrolling transcript and composer.
const DrawerTabPanel = styled(TabPanel)`
    &.react-tabs__tab-panel--selected {
        display: flex;
        flex: 1;
        min-width: 0;
        min-height: 0;
        flex-direction: column;
    }
`;

// The conversation stays in a readable centered column as the drawer widens, like the pricing rules AI drawer; with the
// rail showing it is always on screen, whichever tab was picked while narrow.
const ConversationTabPanel = styled(DrawerTabPanel)`
    align-self: center;
    width: 100%;
    max-width: 820px;

    @container (min-width: 900px) {
        &&[hidden] {
            display: flex !important;
            flex: 1;
            min-height: 0;
            flex-direction: column;
        }
    }
`;

// With the rail showing, the outputs are there: the Outputs tab never shows.
const OutputsTabPanel = styled(DrawerTabPanel)`
    @container (min-width: 900px) {
        && {
            display: none !important;
        }
    }
`;

// The Outputs tab: the same cards as the full-screen rail, as a list.
const OutputsList = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    gap: 6px;
    padding: 16px;
    overflow-y: auto;
`;

// The artifact layout's side panel: the preview lives here beside the conversation instead of inline in the transcript.
// In AI mode the transcript stays in a readable centered column; the drawer fills its narrower width.
const ConversationPanel = styled.section<{ $aiMode: boolean; $centered: boolean }>`
    display: flex;
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    padding: ${(props) => (props.$centered ? '20px 0' : props.$aiMode ? '20px 24px' : '16px')};
    background: ${(props) => (props.$aiMode ? 'transparent' : 'white')};
    /* Full screen: everything stays in a readable column in the middle of the page. The panel itself stays full
       width so the transcript scrollbar sits at the right edge of the screen (see Transcript). */
    ${(props) =>
        props.$centered &&
        `
            /* Doubled class so children with their own margin (error banners, wizard) still follow the column. */
            && > * {
                box-sizing: border-box;
                width: calc(100% - 48px);
                max-width: ${CENTERED_COLUMN_WIDTH_PX}px;
                margin-left: auto;
                margin-right: auto;
            }
        `}
`;

// The preview, shown inline as its own message. Same width as the result-snapshot card and the approval banner, so
// the three line up whichever is on screen.
const InlinePreview = styled.div`
    display: flex;
    width: min(92%, 760px);
    flex-direction: column;
    margin-top: 10px;
    overflow: hidden;
    border: 1px solid ${theme.app.borderColor};
    border-radius: 16px;
    background: #f5f7fa;
`;

// The artifact-switch chips, above the inline preview when the conversation has more than one artifact.
const ArtifactChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
`;

// One chip per artifact of the conversation; the selected one is filled.
const ArtifactChip = styled.button<{ $selected: boolean }>`
    max-width: 220px;
    height: 24px;
    padding: 0 12px;
    overflow: hidden;
    border: 0;
    border-radius: 999px;
    background: ${(props) => (props.$selected ? theme.app.primary_dark : '#eceff1')};
    color: ${(props) => (props.$selected ? 'white' : theme.app.textColor)};
    font-family: inherit;
    font-size: 12px;
    font-weight: 400;
    line-height: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        background: ${(props) => (props.$selected ? theme.app.primary_dark : '#dfe4e7')};
    }

    &:focus-visible {
        outline: 2px solid ${theme.app.primary_light};
        outline-offset: 1px;
    }
`;

// Stacked and left-aligned: the conversation name on top, the New conversation action below it.
const ConversationTools = styled.div<{ $centered?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: ${(props) => (props.$centered ? 'center' : 'flex-start')};
    gap: 4px;
    margin-bottom: 12px;
`;

// Positioning context for the history dropdown, so it hangs from the current conversation name.
const ConversationIdentity = styled.div`
    position: relative;
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 4px;
`;

const ConversationState = styled.span`
    overflow: hidden;
    color: #455a64;
    font-size: 12px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ConversationNameInput = styled.input`
    min-width: 0;
    width: min(320px, 55vw);
    padding: 3px 6px;
    border: 1px solid #a78bfa;
    border-radius: 5px;
    outline: none;
    font-size: 12px;
`;

const ConversationActions = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 4px;
`;

const ConversationToolButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 5px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: #607d8b;
    cursor: pointer;
    font-size: 11px;

    ${Icon},
    ${Icon} svg {
        width: 14px;
        height: 14px;
        min-width: 14px;
        fill: currentColor;
    }

    &:hover:not(:disabled) {
        background: #f5f3ff;
        color: ${theme.ai.solid};
    }

    &:disabled {
        color: #b0bec5;
        cursor: not-allowed;
    }
`;

// Sized like ConversationState so the toolbar stays a quiet single line.
const StartOverButton = styled(ConversationToolButton)`
    padding: 2px 4px;
`;

// The conversation name is renamed by clicking it.
const ConversationNameButton = styled(ConversationState).attrs({ as: 'button' })`
    min-width: 0;
    padding: 2px 4px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    font-family: inherit;
    text-align: left;
    cursor: text;

    &:hover:not(:disabled) {
        background: #f5f3ff;
    }

    &:disabled {
        cursor: not-allowed;
    }
`;

const AccordionChevron = styled(Icon)<{ $open: boolean }>`
    transform: rotate(${(props) => (props.$open ? 180 : 0)}deg);
    transition: transform 0.15s ease;
`;

const GuideHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;

    h3 {
        margin: 0 0 8px;
    }
`;

const GuideExternalLink = styled.a`
    display: flex;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
    background: white;
    color: #6b7280;

    ${Icon},
    ${Icon} svg {
        width: 15px;
        height: 15px;
        min-width: 15px;
        fill: currentColor;
    }

    &:hover {
        color: ${theme.ai.solid};
    }
`;

const Transcript = styled(ChatContainer)<{ $centered: boolean }>`
    gap: 14px;
    padding: 4px 4px 16px;

    /* Centered layout: the scroll area spans the whole panel and the padding centers the messages in the column. */
    ${(props) =>
        props.$centered &&
        `
            && {
                width: 100%;
                max-width: none;
                margin-left: 0;
                margin-right: 0;
                padding-left: max(24px, calc((100% - ${CENTERED_COLUMN_WIDTH_PX}px) / 2));
                padding-right: max(24px, calc((100% - ${CENTERED_COLUMN_WIDTH_PX}px) / 2));
            }
        `}
`;

const Welcome = styled.div`
    display: flex;
    flex: 1;
    min-height: 240px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 12px;
    padding: 32px 18px;
    text-align: center;

    h3 {
        margin: 0;
        color: #263238;
        font-size: 18px;
    }

    p {
        max-width: 420px;
        margin: 0;
        color: #607d8b;
        font-size: 13px;
        line-height: 1.5;
    }
`;

const Suggestions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin-top: 4px;
`;

const MessageGroup = styled.div<{ $merchant?: boolean }>`
    display: flex;
    align-items: ${(props) => (props.$merchant ? 'flex-end' : 'flex-start')};
    flex-direction: column;
    gap: 6px;
`;

const AgentBubble = styled(ChatBubble)`
    max-width: min(92%, 720px);
    white-space: normal;
`;

const AgentMarkdown = styled.div`
    overflow-wrap: anywhere;

    p {
        margin: 0 0 8px;
    }

    p:last-child {
        margin-bottom: 0;
    }

    ul,
    ol {
        margin: 0 0 8px;
        padding-left: 20px;
    }

    li + li {
        margin-top: 4px;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 12px 0 6px;
        color: #263238;
        line-height: 1.3;
    }

    h1 {
        font-size: 18px;
    }

    h2 {
        font-size: 16px;
    }

    h3,
    h4,
    h5,
    h6 {
        font-size: 14px;
    }

    blockquote {
        margin: 8px 0;
        padding-left: 10px;
        border-left: 3px solid #cfd8dc;
        color: #607d8b;
    }

    code {
        padding: 1px 4px;
        border-radius: 4px;
        background: #eceff1;
        font-family: Consolas, Monaco, monospace;
        font-size: 0.92em;
    }

    pre {
        overflow-x: auto;
        margin: 8px 0;
        padding: 10px;
        border-radius: 7px;
        background: #eceff1;

        code {
            padding: 0;
            background: transparent;
        }
    }

    a {
        color: #7b1fa2;
        text-decoration: underline;
        overflow-wrap: anywhere;
    }
`;

const MerchantBubble = styled(ChatBubble)`
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: ${(props) => props.theme.app.backgroundToolbar};
    color: ${(props) => props.theme.app.textColor};
`;

const MessageText = styled.div`
    white-space: pre-wrap;
`;

const SentImages = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    img {
        width: 64px;
        height: 64px;
        border-radius: 7px;
        object-fit: cover;
    }
`;

const Options = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 10px;
`;

const OptionButton = styled(AiOutlineButton)`
    max-width: 100%;
    min-width: 0;
    height: auto;
    min-height: 24px;
    justify-content: flex-start;
    padding: 5px 9px;
    text-align: left;
    white-space: normal;
    overflow-wrap: anywhere;
`;

// Next steps after a completed task: pills that send the step as the merchant's message.
const NextStepChip = styled.button`
    max-width: 100%;
    padding: 5px 12px;
    border: 1px solid ${theme.ai.surfaceBorder};
    border-radius: 999px;
    background: white;
    color: ${theme.ai.solid};
    font-family: inherit;
    font-size: 12px;
    line-height: 1.4;
    text-align: left;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: ${theme.ai.solid};
        background: #faf5ff;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const Notice = styled.div`
    padding: 10px 12px;
    border: 1px solid #ffe0b2;
    border-radius: 9px;
    background: #fff8e1;
    color: #8d6e63;
    font-size: 12px;
`;

// Shared error banner, so its actions keep the banner's own button style.
const ErrorBanner = styled(NotificationBanner)`
    flex-shrink: 0;
    margin: 8px 0;
`;

// The shared approval banner, sized to match the preview and sitting under it.
const PlanBanner = styled(AiApprovalBanner)`
    width: min(92%, 760px);
    margin-top: 12px;
`;

// The single banner under the output: the recap on the left, the actions on the right. Neutral while the draft is
// ready for the catalogue; green once the product has been created.
const ActionBanner = styled.div<{ $created?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    width: min(92%, 760px);
    margin-top: 12px;
    padding: 12px 16px;
    border: 1px solid ${(props) => (props.$created ? '#a5d6a7' : '#e3e7ec')};
    border-radius: 12px;
    background: ${(props) => (props.$created ? '#e8f5e9' : '#f7f9fb')};
`;

const ActionBannerText = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 2px;

    strong {
        color: #263238;
        font-size: 14px;
        font-weight: 600;
    }

    span {
        color: #78909c;
        font-size: 12px;
        line-height: 1.35;
    }
`;

// The two actions share one width whatever their labels say (Create product / printing method / …), so they always
// read as a matched pair. Wide enough that "Request changes" stays on a single line.
const BannerButtons = styled.div`
    display: flex;
    gap: 8px;

    > button {
        flex: 1 1 auto;
        min-width: 176px;
        justify-content: center;
        white-space: nowrap;
    }
`;

const GeneratedImages = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
    margin-top: 12px;
`;

const GeneratedImageCard = styled.div`
    overflow: hidden;
    border: 1px solid #dfe5e8;
    border-radius: 10px;
    background: #fafbfc;

    > a,
    > div:first-child {
        display: block;
        aspect-ratio: 1;
        background: #eceff1;
    }

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const GeneratedImageInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 8px;

    strong {
        color: #263238;
        font-size: 11px;
    }

    span {
        color: #78909c;
        font-size: 10px;
        line-height: 1.35;
    }
`;

const NoCreditsCard = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 10px;
    padding: 10px 12px;
    border: 1px solid #d1c4e9;
    border-radius: 9px;
    background: #f3e5f5;
    color: #4a148c;
    font-size: 11px;

    span {
        line-height: 1.4;
    }
`;

const Composer = styled.div`
    flex-shrink: 0;
`;

// The prompt is a bordered container: the field on top, then a bottom row with the quick-action chips and the
// controls — all inside the prompt.
const ComposerInputArea = styled(ChatInputArea)`
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    padding: 10px;
    padding-top: 10px;

    &:focus-within {
        border-color: ${(props) => props.theme.ai.solid};
    }

    /* The field carries no border of its own; the container provides it. Border-box so the badge's top padding does
       not change the field height while requesting. */
    ${ChatInput} {
        box-sizing: border-box;
        border: 0;
        border-radius: 0;
        padding: 2px 4px;
        resize: none;

        &:focus {
            border-color: transparent;
        }
    }
`;

// Wraps the prompt field so the "requesting changes" badge can sit inside it, top-left. While requesting, the field
// grows taller and leaves room at the top for the badge.
const ComposerField = styled.div<{ $requesting: boolean }>`
    position: relative;
    display: flex;
    flex: 1;
    min-width: 0;

    ${ChatInput} {
        min-height: 44px;

        /* The field keeps its height while requesting; only the top padding grows to clear the badge. */
        ${(props) =>
            props.$requesting &&
            `
            padding-top: 32px;
        `}
    }
`;

const ComposerBottomRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ComposerControls = styled.div`
    display: flex;
    flex: none;
    align-items: center;
    gap: 6px;
    margin-left: auto;
`;

// A small banner (not a pill) inside the prompt, top-left: which product and revision the changes are aimed at.
// Orange-toned to echo the Zakeke action buttons.
const RequestingBadge = styled.span`
    position: absolute;
    top: 7px;
    left: 8px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: calc(100% - 44px);
    padding: 3px 10px;
    border: 1px solid #f6cbb0;
    border-radius: 7px;
    background: #fef2ed;
    color: #9a4a0f;
    font-size: 11.5px;
    font-weight: 500;
    white-space: nowrap;
    pointer-events: none;

    ${Icon},
    ${Icon} svg {
        width: 14px;
        height: 14px;
        flex: none;
        color: #de5900;
    }
`;

const RequestingBadgeText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
`;

// The way out of the change-request mode: a small close control at the top-right of the prompt.
const RequestingExit = styled.button`
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 1;
    display: inline-flex;
    padding: 3px;
    border: 0;
    border-radius: 6px;
    background: none;
    color: #90a4ae;
    cursor: pointer;

    ${Icon},
    ${Icon} svg {
        width: 15px;
        height: 15px;
    }

    &:hover {
        background: #f5f0ec;
        color: #9a4a0f;
    }
`;

// The suggestions row inside the prompt: a label and the quick-action chips.
const PromptSuggestions = styled.div`
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

const PromptSuggestionsLabel = styled.span`
    color: #78909c;
    font-size: 12px;
`;

const QuickActionChip = styled.button`
    height: 26px;
    padding: 0 11px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 999px;
    background: white;
    color: #37474f;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: #90a4ae;
        background: #f5f7fa;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const ComposerDropZone = styled.div<{ $dragging: boolean }>`
    position: relative;
    border-radius: 9px;
    transition:
        background-color 0.15s ease,
        box-shadow 0.15s ease;

    ${(props) =>
        props.$dragging &&
        `
            background: #f5f3ff;
            box-shadow: 0 0 0 2px #c4b5fd;
        `}
`;

const ComposerDropHint = styled.div`
    position: absolute;
    z-index: 1;
    top: 4px;
    left: 50%;
    padding: 3px 9px;
    border-radius: 999px;
    background: ${theme.ai.gradient};
    color: white;
    font-size: 11px;
    font-weight: 600;
    pointer-events: none;
    transform: translateX(-50%);
`;

const Attachments = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 8px 0;
`;

const Attachment = styled.div`
    position: relative;
    display: grid;
    width: 150px;
    min-width: 150px;
    grid-template-columns: 42px 1fr;
    gap: 7px;
    padding: 6px;
    border: 1px solid #dfe5e8;
    border-radius: 9px;
    background: #fafbfc;

    img {
        width: 42px;
        height: 42px;
        border-radius: 6px;
        object-fit: cover;
    }
`;

const AttachmentInfo = styled.div`
    min-width: 0;

    > span {
        display: block;
        overflow: hidden;
        color: #455a64;
        font-size: 10px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    > small {
        color: #78909c;
        font-size: 9px;
    }
`;

const AttachmentSelect = styled.select`
    width: 100%;
    margin-top: 4px;
    border: 1px solid #cfd8dc;
    border-radius: 4px;
    background: white;
    font-size: 9px;
`;

const RemoveAttachment = styled.button`
    position: absolute;
    right: 2px;
    top: 2px;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(38, 50, 56, 0.76);
    color: white;
    cursor: pointer;
    font-size: 12px;
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const AttachButton = styled(AiOutlineButton)`
    flex-shrink: 0;
`;

const findPendingProposal = (draft: AgentDraft) => {
    const suggestions = draft.document?._meta?.suggestions ?? [];
    for (const variant of draft.document?.product?.variants ?? []) {
        for (const side of variant.sides ?? []) {
            if (!side.entityReferenceKey || !side.image?.url || (side.areas?.length ?? 0) > 0) continue;
            if (suggestions.some((suggestion) => suggestion.subject === side.entityReferenceKey && suggestion.value?.widthPx && suggestion.value.heightPx))
                return side;
        }
    }
    return null;
};

const AgentMessageText: FC<{ message: string }> = ({ message }) => {
    const navigate = useNavigate();
    const { close } = useAgentAssistant();
    const html = marked(message.replace(/\\n/g, '\n'), { breaks: true, gfm: true }) as string;

    const handleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        if (!(event.target instanceof Element)) return;
        const link = event.target.closest<HTMLAnchorElement>('a[href]');
        if (!link || !event.currentTarget.contains(link)) return;

        const backofficeOrigin = new URL(config.baseUrl || window.location.href, window.location.href).origin;
        if (!link.pathname.startsWith('/Admin/') || (link.origin !== window.location.origin && link.origin !== backofficeOrigin)) return;

        event.preventDefault();
        close();
        navigate(`${backofficeUrl(link.pathname.slice('/Admin'.length))}${link.search}${link.hash}`);
    };

    return <AgentMarkdown onClick={handleClick} dangerouslySetInnerHTML={{ __html: html }} />;
};

const uploadId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isImageFile = (file: File | null): file is File => Boolean(file?.type.startsWith('image/'));

const getImageFilesFromDataTransfer = (dataTransfer: DataTransfer | null) => {
    if (!dataTransfer) return [];

    const itemFiles = Array.from(dataTransfer.items ?? [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter(isImageFile);

    if (itemFiles.length > 0) return itemFiles;
    return Array.from(dataTransfer.files ?? []).filter(isImageFile);
};

const containsFiles = (dataTransfer: DataTransfer) =>
    Array.from(dataTransfer.types ?? []).includes('Files') ||
    dataTransfer.files.length > 0 ||
    Array.from(dataTransfer.items ?? []).some((item) => item.kind === 'file');

export interface AgentAssistantProps {
    onExternalChanges?: () => void;
    /** Starts a new, empty conversation with this message. Restored conversations are never changed. */
    initialPrompt?: string;
    guide?: AgentGuide;
    /** Full screen: the conversation tools (name, history, new conversation) portal into this header slot to free space. */
    headerToolsSlot?: HTMLElement | null;
}

export const AgentAssistant: FC<AgentAssistantProps> = ({ onExternalChanges, initialPrompt, guide: initialGuide, headerToolsSlot }) => {
    const {
        conversationId,
        conversationName,
        historyRevision,
        currentRequest,
        isCancelling,
        cancelRequest,
        isRefreshingResult,
        sendAnswers,
        entries,
        guide,
        draft,
        isBusy,
        isDraftBusy,
        isDraftRefreshRequired,
        refreshPreview,
        isRestoring,
        error,
        canRetry,
        hasPendingRequest,
        send,
        retry,
        upsertPrintArea,
        replaceSideImage,
        deletePrintArea,
        restorePrintArea,
        setSideScale,
        openConversation,
        renameConversation,
        syncConversationName,
        reset
    } = useAgentSession(onExternalChanges, initialGuide);
    const { showDialog, closeDialog } = useDialogManager();
    const { showSideImagesNoCreditsDialog } = useSideImagesNoCreditsDialog();
    const { runWithPreflight } = useGenerationPreflight();
    const { isFullScreen, clearInitialPrompt, close: closeAssistant, sidebarOptions } = useAgentAssistant();
    const [answerCorrection, setAnswerCorrection] = useState<{ questionnaire: AgentQuestionnaire; recap: AgentAnswerRecap; afterTurnId: number } | null>(null);
    // The kind of plan being applied after the merchant approved it; cleared when the agent's reply ends (or fails).
    const [applyingPlanKind, setApplyingPlanKind] = useState<AgentArtifactKind | 'unknown' | null>(null);
    // However the plan was approved (the banner, an option, a typed reply), the agent's own apply steps say it is applying.
    const applyingActivityKind = isBusy
        ? ([...(currentRequest?.progress?.activities ?? [])]
              .reverse()
              .map((activity) => (activity.code.startsWith('apply_') ? (APPLY_CODE_ARTIFACTS[activity.code] ?? 'unknown') : null))
              .find(Boolean) ?? null)
        : null;
    const applyingKind = applyingPlanKind ?? applyingActivityKind;
    const applyingPlanPhrases = applyingKind ? APPLYING_PLAN_PHRASES[applyingKind] : undefined;
    const wasBusyRef = useRef(false);
    // When a reply ends: the applying phrases stop and, in the drawer, a created product ends the work: the drawer closes
    // and the page shows it (the caller's handler, or refreshed data). Only replies to this session count, never a
    // restored conversation.
    useEffect(() => {
        if (isBusy) {
            wasBusyRef.current = true;
            return;
        }
        const replyEnded = wasBusyRef.current;
        if (!replyEnded && !error) return;
        wasBusyRef.current = false;
        setApplyingPlanKind(null);
        if (!replyEnded || isFullScreen || !lastAgentTurn || !turnCreatedProduct(lastAgentTurn, draft)) return;
        // Keep agent-proposed actions visible until the merchant chooses what to do.
        if (lastAgentTurn.suggestedActions?.length) return;
        closeAssistant();
        if (sidebarOptions?.onProductCreated) {
            sidebarOptions.onProductCreated(createdProductId(draft, lastAgentTurn.message));
            return;
        }
        const relayStore = useRelayStore.getState();
        if (relayStore.isRelayStoreDirty) relayStore.incrementRelayStoreKey();
    }, [isBusy, error]);
    const hasPreview = hasUsefulAgentPreview(draft);
    const existingArtifactKinds = hasPreview ? getAgentArtifacts(draft).map((item) => item.kind) : [];
    // The artifact the agent is creating right now: the latest artifact activity whose artifact does not exist yet.
    const buildingArtifactKind = isBusy
        ? ([...(currentRequest?.progress?.activities ?? [])]
              .reverse()
              .filter((activity) => activity.state !== 'failed')
              .map((activity) => ARTIFACT_KIND_BY_ACTIVITY[activity.code])
              .find((kind) => kind && !existingArtifactKinds.includes(kind)) ?? null)
        : null;
    const isBuildingArtifact = buildingArtifactKind !== null;
    const showArtifactPanel = hasPreview || isBuildingArtifact;
    const [dismissedIntroductions, setDismissedIntroductions] = useState<AgentArtifactKind[]>([]);
    const [createdArtifactKind, setCreatedArtifactKind] = useState<AgentArtifactKind | null>(null);
    // "Request changes" and the gesture card's "Describe" focus the composer so the merchant describes the change.
    const composerRef = useRef<HTMLTextAreaElement>(null);
    const focusComposer = () => composerRef.current?.focus();
    const artifacts = getAgentArtifacts(draft);
    const artifactKinds = artifacts.map((item) => item.kind).join(',');
    const catalogueArtifactKinds = new Set<AgentArtifactKind>();
    if (draft.document?.printingMethodSetup?.printingMethod?.printingMethodId) catalogueArtifactKinds.add('printingMethod');
    if (draft.document?.pricingSetup?.pricingGroup?.pricingGroupId) catalogueArtifactKinds.add('pricing');
    for (const item of draft.drafts ?? []) if (item.appliedRevisionNumber != null) catalogueArtifactKinds.add(item.kind as AgentArtifactKind);
    const createdArtifactKinds = new Set<AgentArtifactKind>(artifacts.filter((item) => item.status === 'created').map((item) => item.kind));
    const artifactStatus = (kind: AgentArtifactKind): 'draft' | 'created' | 'pending' => artifacts.find((item) => item.kind === kind)?.status ?? 'draft';
    const [selectedArtifact, setSelectedArtifact] = useState<AgentArtifactKind | null>(null);
    const turnById = new Map<number, AgentTurn>();
    for (const entry of entries) {
        if (entry.role === 'agent' && entry.turn.turnId != null) turnById.set(entry.turn.turnId, entry.turn);
    }
    const { resultOutputs, statuses: resultStatuses } = buildResultRail(entries);
    const visibleResultIds = new Set(resultOutputs.map((result) => result.id));
    const hasOutputs = artifacts.length > 0 || isBuildingArtifact || resultOutputs.length > 0;
    const resultKind = (result: Extract<AgentChatEntry, { role: 'draft-result' }>): AgentArtifactKind | undefined =>
        result.kind as AgentArtifactKind | undefined;
    const revisionResults = resultOutputs;
    const revByResultId = new Map(resultOutputs.map((result) => [result.id, result.resultNumber]));
    const latestResultIdByKind = new Map<AgentArtifactKind, string>();
    for (const result of resultOutputs) {
        const kind = resultKind(result);
        if (kind) latestResultIdByKind.set(kind, result.id);
    }
    const displayedResultNumber = (kind: AgentArtifactKind | undefined) => {
        const currentDraftId = draft.drafts?.find((item) => item.kind === kind)?.draftId;
        const persisted = draft.publishedDrafts?.find((item) => item.draftId === currentDraftId)?.resultNumber;
        if (persisted != null) return persisted;
        const latestId = kind ? latestResultIdByKind.get(kind) : undefined;
        return (latestId ? revByResultId.get(latestId) : undefined) ?? draft.resultNumber;
    };
    // Drawer tabs: which one is shown.
    const [drawerTab, setDrawerTab] = useState(DRAWER_TAB_CONVERSATION);
    const inlinePreviewRef = useRef<HTMLDivElement>(null);
    // A new conversation has no outputs yet: the drawer goes back to the conversation.
    useEffect(() => {
        if (!hasOutputs) setDrawerTab(DRAWER_TAB_CONVERSATION);
    }, [hasOutputs]);
    const knownArtifactKindsRef = useRef<string[]>([]);
    const conversationIdentityRef = useRef<HTMLDivElement>(null);
    // A newly created artifact (e.g. a printing method after the product) is brought into view; later the merchant switches freely.
    useEffect(() => {
        const kinds = artifactKinds ? artifactKinds.split(',') : [];
        const added = kinds.filter((kind) => !knownArtifactKindsRef.current.includes(kind));
        knownArtifactKindsRef.current = kinds;
        if (added.length > 0) {
            setSelectedArtifact(added[added.length - 1] as AgentArtifactKind);
            // Artifacts loaded with a restored conversation were not created now: they get no introduction.
            if (!isRestoring) setCreatedArtifactKind(added[added.length - 1] as AgentArtifactKind);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artifactKinds]);
    const activeArtifact = artifacts.find((item) => item.kind === selectedArtifact)?.kind ?? artifacts[artifacts.length - 1]?.kind;
    // Introduces the artifact being created, then the one just created while it is the one on screen, until dismissed.
    const introducedCandidate = buildingArtifactKind ?? (createdArtifactKind === activeArtifact ? createdArtifactKind : null);
    const introducedArtifactKind = introducedCandidate && !dismissedIntroductions.includes(introducedCandidate) ? introducedCandidate : null;
    const hasConversation = Boolean(conversationId) || entries.length > 0;
    const [message, setMessage] = useState('');
    // "Request changes" puts the composer into a visible "describe your changes" state.
    const [isRequestingChanges, setIsRequestingChanges] = useState(false);
    const requestChanges = () => {
        setIsRequestingChanges(true);
        focusComposer();
    };
    // Quick actions inside the prompt while requesting changes: shortcuts that fill the message. They are built from the
    // output on screen — the real print areas, sides, rules — so they always fit what can actually be changed.
    const composerQuickActions = ((): Array<{ label: string; message: string }> => {
        const productDoc = draft.document?.product;
        const printingMethodDoc = draft.document?.printingMethodSetup?.printingMethod;
        const pricingDoc = draft.document?.pricingSetup?.pricingGroup;

        if (activeArtifact === 'printingMethod' && printingMethodDoc) {
            const actions = [
                { label: T._('Change output settings', 'Admin'), message: T._('I want to change the output settings of this printing method.', 'Admin') },
                {
                    label: T._('Change what customers can add', 'Admin'),
                    message: T._('I want to change what customers can add with this printing method, like text or images.', 'Admin')
                }
            ];
            actions.push({
                label: printingMethodDoc.dpi
                    ? stringFormat(T._('Change resolution ({0} DPI)', 'Admin'), String(printingMethodDoc.dpi))
                    : T._('Change resolution', 'Admin'),
                message: T._('I want to change the resolution (DPI) of this printing method.', 'Admin')
            });
            return actions;
        }

        if (activeArtifact === 'pricing' && pricingDoc) {
            const actions: Array<{ label: string; message: string }> = [];
            const firstRule = pricingDoc.rules?.[0];
            const ruleTitle = firstRule?.elementTitle || (firstRule?.propertyId ? `${T._('Property', 'Admin')} #${firstRule.propertyId}` : null);
            if (ruleTitle)
                actions.push({
                    label: stringFormat(T._('Edit "{0}"', 'Admin'), ruleTitle),
                    message: stringFormat(T._('I want to edit the "{0}" pricing rule.', 'Admin'), ruleTitle)
                });
            actions.push({ label: T._('Add a rule', 'Admin'), message: T._('I want to add a pricing rule.', 'Admin') });
            actions.push({ label: T._('Change a price', 'Admin'), message: T._('I want to change a price in these pricing rules.', 'Admin') });
            return actions;
        }

        if (productDoc?.isResizable) {
            return [
                { label: T._('Change size limits', 'Admin'), message: T._('I want to change the allowed dimensions of this resizable product.', 'Admin') },
                { label: T._('Change proportions', 'Admin'), message: T._('I want to change whether this resizable product keeps its aspect ratio.', 'Admin') },
                { label: T._('Change background', 'Admin'), message: T._('I want to change the background of this resizable product.', 'Admin') }
            ];
        }
        if (productDoc) {
            const firstSide = productDoc.variants?.[0]?.sides?.[0];
            const firstArea = firstSide?.areas?.[0];
            const sideName = firstSide?.name || T._('the main side', 'Admin');
            const areaName = firstArea?.name || firstArea?.label;
            const actions: Array<{ label: string; message: string }> = [];
            if (areaName)
                actions.push({
                    label: stringFormat(T._('Resize "{0}"', 'Admin'), areaName),
                    message: stringFormat(T._('I want to resize the "{0}" print area on the "{1}" side.', 'Admin'), areaName, sideName)
                });
            actions.push({ label: T._('Add a print area', 'Admin'), message: T._('I want to add a print area on this product.', 'Admin') });
            if (firstSide)
                actions.push({
                    label: stringFormat(T._('Replace "{0}" image', 'Admin'), sideName),
                    message: stringFormat(T._('I want to replace the image of the "{0}" side.', 'Admin'), sideName)
                });
            return actions;
        }

        return [];
    })();
    const [attachments, setAttachments] = useState<UploadingAttachment[]>([]);
    const [composerError, setComposerError] = useState<string | null>(null);
    const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);
    const [isRetryPreflightInProgress, setIsRetryPreflightInProgress] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isRenamingConversation, setIsRenamingConversation] = useState(false);
    const [conversationNameDraft, setConversationNameDraft] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const transcriptRef = useRef<HTMLDivElement>(null);
    const attachmentDragDepthRef = useRef(0);
    const sentInitialPromptRef = useRef<string | null>(null);
    const hasScrolledToEndRef = useRef(false);
    const normalizedInitialPrompt = initialPrompt?.trim() ?? '';

    const variants = draft.document?.product?.variants ?? [];
    const variantNames = [...new Set(variants.map((variant) => variant.name).filter((name): name is string => Boolean(name)))];
    const sideNames = [
        ...new Set(
            variants
                .flatMap((variant) => variant.sides ?? [])
                .map((side) => side.name)
                .filter((name): name is string => Boolean(name))
        )
    ];
    const latestAgentEntry = [...entries].reverse().find((entry) => entry.role === 'agent');
    const latestDraftResultEntry = resultOutputs[resultOutputs.length - 1];
    const latestPublishedDraftRevision = latestDraftResultEntry ? turnById.get(latestDraftResultEntry.turnId)?.draftRevisionNumber : null;
    // Each rev card scrolls its result card into view in the conversation.
    const resultRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const [activeResultId, setActiveResultId] = useState<string | null>(null);
    const activeResultKey = activeResultId ?? latestDraftResultEntry?.id ?? null;
    const navigateToResult = (id: string) => {
        setActiveResultId(id);
        // The drawer must be on the conversation tab to show the result card before scrolling to it.
        if (!isFullScreen) setDrawerTab(DRAWER_TAB_CONVERSATION);
        window.setTimeout(() => resultRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };
    // The latest generation reported for each side: the artifact explains a missing image with it.
    const imageGenerations = useMemo(() => {
        const bySide: Record<string, AgentGeneratedImage> = {};
        entries.forEach((entry) => {
            if (entry.role !== 'agent') return;
            entry.turn.generatedImages?.forEach((image) => {
                bySide[image.sideRef] = image;
            });
        });
        return bySide;
    }, [entries]);
    const lastAgentTurn = latestAgentEntry?.role === 'agent' ? latestAgentEntry.turn : null;
    // While the agent works, the artifact it is creating or revising is the one on screen (e.g. a printing method under revision).
    const workingArtifactKind = isBusy
        ? ([...(currentRequest?.progress?.activities ?? [])]
              .reverse()
              .map((activity) => WORKED_ARTIFACT_BY_CODE[activity.code])
              .find(Boolean) ?? null)
        : null;
    useEffect(() => {
        if (workingArtifactKind && artifactKinds.split(',').includes(workingArtifactKind)) setSelectedArtifact(workingArtifactKind);
    }, [workingArtifactKind, artifactKinds]);
    // When a reply arrives, it shows the artifact it worked on: the plan it asks to approve, or the last result it reports.
    useEffect(() => {
        if (isRestoring || !lastAgentTurn) return;
        const worked =
            (lastAgentTurn.approval && approvalArtifactKind(lastAgentTurn.approval)) ??
            [...(lastAgentTurn.resultRecap?.items ?? [])]
                .reverse()
                .map((item) => WORKED_ARTIFACT_BY_CODE[item.code])
                .find(Boolean) ??
            null;
        if (worked && artifactKinds.split(',').includes(worked)) setSelectedArtifact(worked);
        // Only on a new reply: afterwards the merchant switches freely.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestAgentEntry?.id, artifactKinds]);
    const hasLaterSubmission = latestAgentEntry
        ? entries
              .slice(entries.findIndex((entry) => entry.id === latestAgentEntry.id) + 1)
              .some((entry) => entry.role === 'merchant' && entry.requestId && !entry.cancelled)
        : false;
    const activeQuestionnaire = !hasLaterSubmission && hasQuestionnaireChoices(lastAgentTurn?.questionnaire) ? lastAgentTurn?.questionnaire : null;
    const correctionWasSent =
        answerCorrection &&
        entries.some(
            (entry) =>
                entry.role === 'merchant' &&
                !entry.cancelled &&
                entry.answers?.turnId === answerCorrection.questionnaire.turnId &&
                entry.answers?.correctionAfterTurnId === answerCorrection.afterTurnId
        );
    const wizardQuestionnaire = !correctionWasSent && answerCorrection ? answerCorrection.questionnaire : activeQuestionnaire;
    useEffect(() => {
        if (!conversationId || isRestoring) return;
        const prefix = `zakeke.agent.questions.${conversationId}.`;
        const activeKey = wizardQuestionnaire ? `${prefix}${wizardQuestionnaire.turnId}.${answerCorrection?.afterTurnId ?? 'new'}` : null;
        try {
            Object.keys(window.sessionStorage).forEach((key) => {
                if (key.startsWith(prefix) && key !== activeKey) window.sessionStorage.removeItem(key);
            });
        } catch {
            // Draft storage is optional.
        }
    }, [conversationId, isRestoring, wizardQuestionnaire?.turnId, answerCorrection?.afterTurnId]);
    const pendingProposal = useMemo(() => findPendingProposal(draft), [draft]);
    const isInteractionBusy = isBusy || hasPendingRequest || isDraftBusy || isRetryPreflightInProgress;
    useEffect(() => {
        if (isInteractionBusy || isRestoring || isHistoryOpen || isRenamingConversation || (!isFullScreen && drawerTab !== DRAWER_TAB_CONVERSATION)) return;
        const frame = window.requestAnimationFrame(() => composerRef.current?.focus({ preventScroll: true }));
        return () => window.cancelAnimationFrame(frame);
    }, [conversationId, isInteractionBusy, isRestoring, isHistoryOpen, isRenamingConversation, isFullScreen, drawerTab]);
    const isRequestInProgress = currentRequest?.state === 'queued' || currentRequest?.state === 'running';
    const showStopButton = isRequestInProgress || (isBusy && !currentRequest);
    const canStopRequest = isRequestInProgress && !currentRequest?.cancellationRequested && !isCancelling;
    const appendVoiceTranscript = (transcript: string) => {
        setMessage((current) => `${current}${current && !/\s$/.test(current) ? ' ' : ''}${transcript}`);
        setComposerError(null);
    };
    const handleVoiceInputError = (voiceError: string) => {
        const messageByError: Record<string, string> = {
            'not-allowed': T._('Microphone access was denied. Allow access in your browser settings and try again.', 'Admin'),
            'service-not-allowed': T._('Microphone access was denied. Allow access in your browser settings and try again.', 'Admin'),
            'audio-capture': T._('No microphone was found. Check your audio input and try again.', 'Admin'),
            'no-speech': T._('No speech was detected. Try again and speak closer to the microphone.', 'Admin')
        };
        setComposerError(messageByError[voiceError] ?? T._('Voice input could not be started. Please try again.', 'Admin'));
    };
    const {
        isSupported: isVoiceInputSupported,
        isListening,
        abortListening,
        toggleListening
    } = useSpeechRecognition({
        disabled: isInteractionBusy,
        onTranscript: appendVoiceTranscript,
        onError: handleVoiceInputError
    });

    const retryFailedImages = (images: AgentGeneratedImage[]) => {
        if (images.length === 0 || isInteractionBusy || isRetryPreflightInProgress) return;

        setIsRetryPreflightInProgress(true);

        const retryMessage = T._('Retry the image generation using the same details.', 'Admin');

        void runWithPreflight(
            async () => {
                await send(retryMessage);
            },
            {
                onNoCredits: showSideImagesNoCreditsDialog
            }
        ).finally(() => setIsRetryPreflightInProgress(false));
    };

    useEffect(() => {
        // Until the conversation starts the transcript holds only the guide or the welcome, which must be read from the top.
        if (entries.length === 0 && !isBusy) {
            hasScrolledToEndRef.current = false;
            return;
        }

        const transcript = transcriptRef.current;
        if (!transcript) return;

        // A conversation that is already under way (sidebar reopened, full screen toggled) opens straight at its end;
        // only the messages that arrive afterwards scroll smoothly.
        transcript.scrollTo({ top: transcript.scrollHeight, behavior: hasScrolledToEndRef.current ? 'smooth' : 'auto' });
        hasScrolledToEndRef.current = true;
        // draft/preview deps: the inline preview and skeleton grow the transcript without adding an entry, so they must scroll too.
    }, [entries, isBusy, attachments.length, draft, showArtifactPanel, isBuildingArtifact]);

    // Entering the change-request state grows the prompt, which would otherwise cover the banner above it: scroll the
    // transcript to the end so the output and its banner stay visible over the taller prompt.
    useEffect(() => {
        if (!isRequestingChanges) return;
        const transcript = transcriptRef.current;
        if (!transcript) return;
        const scrollToEnd = () => transcript.scrollTo({ top: transcript.scrollHeight, behavior: 'smooth' });
        scrollToEnd();
        const timer = window.setTimeout(scrollToEnd, 200);
        return () => window.clearTimeout(timer);
    }, [isRequestingChanges]);

    // A freshly created output (a new result) is scrolled fully into view, down to its Request changes / Create banner.
    // The result card loads its snapshot asynchronously, so the transcript keeps growing after the change above ran:
    // re-scroll a couple of times to follow it until the banner below the output is on screen.
    const lastResultCountRef = useRef(resultOutputs.length);
    useEffect(() => {
        const count = resultOutputs.length;
        const isNewOutput = count > lastResultCountRef.current;
        lastResultCountRef.current = count;
        if (!isNewOutput) return;
        const transcript = transcriptRef.current;
        if (!transcript) return;
        const scrollToBanner = () => transcript.scrollTo({ top: transcript.scrollHeight, behavior: 'smooth' });
        scrollToBanner();
        const timers = [250, 600, 1000].map((delay) => window.setTimeout(scrollToBanner, delay));
        return () => timers.forEach((timer) => window.clearTimeout(timer));
    }, [resultOutputs.length]);

    useEffect(() => {
        if (
            !normalizedInitialPrompt ||
            isRestoring ||
            isBusy ||
            conversationId !== null ||
            entries.length > 0 ||
            sentInitialPromptRef.current === normalizedInitialPrompt
        )
            return;

        sentInitialPromptRef.current = normalizedInitialPrompt;
        clearInitialPrompt();
        void send(normalizedInitialPrompt);
    }, [clearInitialPrompt, conversationId, entries.length, isBusy, isRestoring, normalizedInitialPrompt, send]);

    const updateAttachment = (id: string, values: Partial<UploadingAttachment>) => {
        setAttachments((current) => current.map((attachment) => (attachment.id === id ? { ...attachment, ...values } : attachment)));
    };

    const uploadAttachmentFiles = async (files: File[]) => {
        if (isInteractionBusy) return;

        const imageFiles = files.filter(isImageFile);
        if (imageFiles.length === 0) return;
        setComposerError(null);

        for (const file of imageFiles) {
            if (file.size > MAX_ATTACHMENT_BYTES) {
                setComposerError(T._('Images must be smaller than 12 MB.', 'Admin'));
                continue;
            }

            const id = uploadId();
            const previewUrl = URL.createObjectURL(file);
            setAttachments((current) => [...current, { id, name: file.name, url: '', previewUrl, status: 'uploading' }]);

            try {
                const url = await uploadFile(file);
                updateAttachment(id, { url, status: 'ready' });
            } catch {
                updateAttachment(id, { status: 'failed', error: T._('Upload failed', 'Admin') });
            }
        }
    };

    const attachFiles = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        void uploadAttachmentFiles(files);
    };

    const handleComposerDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (isInteractionBusy || !containsFiles(event.dataTransfer)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        attachmentDragDepthRef.current += 1;
        setIsDraggingAttachments(true);
    };

    const handleComposerDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (isInteractionBusy || !containsFiles(event.dataTransfer)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsDraggingAttachments(true);
    };

    const handleComposerDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (!containsFiles(event.dataTransfer)) return;
        event.preventDefault();
        attachmentDragDepthRef.current = Math.max(attachmentDragDepthRef.current - 1, 0);
        if (attachmentDragDepthRef.current === 0) setIsDraggingAttachments(false);
    };

    const handleComposerDrop = (event: DragEvent<HTMLDivElement>) => {
        if (!containsFiles(event.dataTransfer)) return;
        event.preventDefault();
        attachmentDragDepthRef.current = 0;
        setIsDraggingAttachments(false);
        void uploadAttachmentFiles(getImageFilesFromDataTransfer(event.dataTransfer));
    };

    const handleComposerPaste = (event: ClipboardEvent<HTMLDivElement>) => {
        if (isInteractionBusy) return;
        const imageFiles = getImageFilesFromDataTransfer(event.clipboardData);
        if (imageFiles.length === 0) return;
        event.preventDefault();
        void uploadAttachmentFiles(imageFiles);
    };

    const removeAttachment = (id: string) => {
        setAttachments((current) => {
            const attachment = current.find((item) => item.id === id);
            if (attachment) URL.revokeObjectURL(attachment.previewUrl);
            return current.filter((item) => item.id !== id);
        });
    };

    const sendCurrentMessage = () => {
        if (attachments.some((attachment) => attachment.status === 'uploading')) {
            setComposerError(T._('Wait for the images to finish uploading.', 'Admin'));
            return;
        }

        const readyAttachments = attachments.filter((attachment) => attachment.status === 'ready');
        if (!message.trim() && readyAttachments.length === 0) return;
        abortListening();
        void send(message, readyAttachments);
        setAnswerCorrection(null);
        attachments.filter((attachment) => attachment.status !== 'ready').forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
        setMessage('');
        setAttachments([]);
        setComposerError(null);
        setIsRequestingChanges(false);
    };

    const submitGesture = (gestureMessage: string, gesture?: AgentGesture | null) => {
        void send(gestureMessage, [], gesture ?? null);
    };

    const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendCurrentMessage();
        }
    };

    // Applying an approved plan: until the reply arrives, the status rotates phrases about what is being created.
    const applyPlan = (kind: AgentArtifactKind | 'unknown', message: string) => {
        setApplyingPlanKind(kind);
        void send(message);
    };
    const confirmPlan = (kind: AgentArtifactKind | 'unknown') =>
        applyPlan(kind, lastAgentTurn?.defaultMessages?.confirm ?? T._('I approve the proposed plan. Apply it to the catalogue.', 'Admin'));
    const revisePlan = () => void send(lastAgentTurn?.defaultMessages?.revise ?? T._('Do not apply the proposed changes. I want to revise the plan.', 'Admin'));

    const clearComposer = () => {
        abortListening();
        attachments.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
        setAttachments([]);
        setMessage('');
        setComposerError(null);
    };

    const startNewConversation = () => {
        clearComposer();
        setAnswerCorrection(null);
        setIsHistoryOpen(false);
        setDrawerTab(DRAWER_TAB_CONVERSATION);
        reset();
        window.requestAnimationFrame(focusComposer);
    };

    // "Create another product" must start a brand-new conversation, not continue this one (which would just re-show the
    // product just created). reset() clears the conversation asynchronously, so the message is queued and sent once the
    // conversation is fresh (conversationId back to null), which makes send() open a new conversation for it.
    const pendingFreshSendRef = useRef<string | null>(null);
    useEffect(() => {
        if (conversationId === null && pendingFreshSendRef.current && !isBusy && !isRestoring) {
            const message = pendingFreshSendRef.current;
            pendingFreshSendRef.current = null;
            void send(message);
        }
    }, [conversationId, isBusy, isRestoring, send]);
    const createAnotherProduct = (message: string) =>
        confirmDiscardComposer(() => {
            startNewConversation();
            pendingFreshSendRef.current = message;
        });

    const confirmDiscardComposer = (action: () => void) => {
        if (!message.trim() && attachments.length === 0) {
            action();
            return;
        }

        showDialog(
            'agent-discard-composer',
            <QuestionDialog
                title={T._('Discard unsent message?', 'Admin')}
                message={T._('The message and attachments in the composer will be lost.', 'Admin')}
                buttonYesLabel={T._('Discard', 'Admin')}
                buttonNoLabel={T._('Cancel', 'Admin')}
                destructive
                onYesClick={() => {
                    closeDialog('agent-discard-composer');
                    action();
                }}
                onNoClick={() => closeDialog('agent-discard-composer')}
            />
        );
    };

    const restart = () => confirmDiscardComposer(startNewConversation);

    const selectConversation = (id: number) => {
        if (id === conversationId) {
            setIsHistoryOpen(false);
            return;
        }
        confirmDiscardComposer(() => {
            clearComposer();
            setAnswerCorrection(null);
            void openConversation(id).then((opened) => {
                if (opened) setIsHistoryOpen(false);
            });
        });
    };

    const beginConversationRename = () => {
        setConversationNameDraft(conversationName ?? '');
        setIsRenamingConversation(true);
    };

    const commitConversationRename = async () => {
        const name = conversationNameDraft.trim();
        if (!name || name === conversationName || !conversationId) {
            setIsRenamingConversation(false);
            return;
        }
        if (await renameConversation(name)) setIsRenamingConversation(false);
    };

    // Deleting the open conversation leaves an empty one; the history stays open and the composer keeps what was typed.
    const handleConversationDeleted = (id: number) => {
        if (id !== conversationId) return;
        setAnswerCorrection(null);
        reset();
    };

    const closeHistory = useCallback(() => setIsHistoryOpen(false), []);

    const conversationHistory = (
        <AgentConversationHistory
            id={CONVERSATION_HISTORY_ID}
            anchorRef={conversationIdentityRef}
            activeConversationId={conversationId}
            disabled={isInteractionBusy || isRestoring}
            revision={historyRevision}
            onClose={closeHistory}
            onSelectConversation={selectConversation}
            onRenamed={syncConversationName}
            onDeleted={handleConversationDeleted}
        />
    );

    // A plan on the latest turn still waiting to be applied.
    const pendingOfferHashes = new Set(
        (draft.drafts ?? [])
            .map((item) => (item.document as AgentDraftDocument | null)?._meta?.approvalOffer?.scopeHash)
            .filter((hash): hash is string => Boolean(hash))
    );
    const earlierApproval = [...entries]
        .reverse()
        .find((entry) => entry.role === 'agent' && entry.turn.approval && pendingOfferHashes.has(entry.turn.approval.scopeHash));
    const pendingApproval =
        lastAgentTurn && !turnAppliedChanges(lastAgentTurn)
            ? (lastAgentTurn.approval ??
              (earlierApproval?.role === 'agent' ? earlierApproval.turn.approval : null) ??
              (draft.document?._meta?.approvalOffer && draft.document._meta.approvalOffer.plannedInTurn === lastAgentTurn.turnNumber
                  ? draft.document._meta.approvalOffer
                  : null))
            : null;

    // The banner adapts to the output on screen (product, printing method or pricing): its recap and
    // its created state all follow the active artifact. Counts come from the current draft (a result carries only its rev).
    const bannerKind = activeArtifact ?? 'product';
    const bannerProduct = draft.document?.product;
    const bannerVariantCount = bannerProduct?.variants?.length ?? 0;
    const bannerAreaCounts = (bannerProduct?.variants ?? []).map((variant) =>
        (variant.sides ?? []).reduce((total, side) => total + (side.areas?.length ?? 0), 0)
    );
    const bannerAreaCount =
        bannerAreaCounts.length > 0 && bannerAreaCounts.every((count) => count === bannerAreaCounts[0])
            ? bannerAreaCounts[0]
            : bannerAreaCounts.reduce((total, count) => total + count, 0);
    const bannerCreated = createdArtifactKinds.has(bannerKind);
    const bannerBlocked = Boolean(
        lastAgentTurn?.operationRequirements?.length || lastAgentTurn?.requiredPlanFeatures?.length || lastAgentTurn?.requiredAddons?.length
    );
    const bannerRecap = (statusWord: string) => {
        const currentResultNumber = displayedResultNumber(bannerKind);
        const parts = [`${statusWord}${currentResultNumber != null ? ` ${T._('rev.', 'Admin')}${currentResultNumber}` : ''}`];
        if (bannerKind === 'product' && bannerProduct?.isResizable) {
            parts.push(T._('Resizable product', 'Admin'));
            const count = bannerProduct.resizable?.sides?.length ?? 0;
            parts.push(`${count} ${count === 1 ? T._('surface', 'Admin') : T._('surfaces', 'Admin')}`);
        } else if (bannerKind === 'product') {
            if (bannerVariantCount > 0) parts.push(`${bannerVariantCount} ${bannerVariantCount === 1 ? T._('variant', 'Admin') : T._('variants', 'Admin')}`);
            parts.push(`${bannerAreaCount} ${bannerAreaCount === 1 ? T._('print area', 'Admin') : T._('print areas', 'Admin')}`);
        } else if (bannerKind === 'pricing') {
            const ruleCount = draft.document?.pricingSetup?.pricingGroup?.rules?.length ?? 0;
            if (ruleCount > 0) parts.push(`${ruleCount} ${ruleCount === 1 ? T._('rule', 'Admin') : T._('rules', 'Admin')}`);
        }
        return parts.join(' · ');
    };
    // Creation is offered only by the plan approval banner. An unplanned draft can request changes,
    // but must not expose a Create action that prepares a plan and then asks for approval again.
    // One banner under the output: a recap of the draft. Once the
    // output is created it turns green. It is gone while a change request is being written (the merchant is already
    // acting on the output) and while questions are still pending (the flow stays linear: answer first, then decide).
    const actionQuestion =
        isRequestingChanges || wizardQuestionnaire ? null : pendingApproval ? (
            <PlanBanner
                title={T._('Approval required', 'Admin')}
                message={pendingApproval.description || T._('Review the plan in the preview above.', 'Admin')}
                reviseLabel={T._('Revise', 'Admin')}
                confirmLabel={confirmApprovalLabel(pendingApproval, catalogueArtifactKinds.has(approvalArtifactKind(pendingApproval) ?? bannerKind))}
                onRevise={revisePlan}
                onConfirm={() => confirmPlan(approvalArtifactKind(pendingApproval) ?? bannerKind)}
                disabled={isInteractionBusy}
            />
        ) : bannerCreated ? (
            <ActionBanner $created>
                <ActionBannerText>
                    <strong>{T._('Applied to your catalogue', 'Admin')}</strong>
                    <span>{bannerRecap(T._('Applied', 'Admin'))}</span>
                </ActionBannerText>
            </ActionBanner>
        ) : (
            <ActionBanner>
                <ActionBannerText>
                    <strong>{T._('Draft not applied', 'Admin')}</strong>
                    <span>
                        {bannerRecap(T._('Draft', 'Admin'))} ·{' '}
                        {bannerBlocked
                            ? T._('Resolve the requirements shown in the chat before applying these changes.', 'Admin')
                            : T._('These changes have not been saved to your catalogue.', 'Admin')}
                    </span>
                </ActionBannerText>
                <BannerButtons>
                    <BasicButton type='button' $size='medium' disabled={isInteractionBusy} onClick={requestChanges}>
                        <Icon>
                            <ChatBubbleOutlineIcon />
                        </Icon>
                        {T._('Request changes', 'Admin')}
                    </BasicButton>
                </BannerButtons>
            </ActionBanner>
        );

    // The preview (the output) shows the entity and status pills; its quick actions are not inside it, but asked
    // as a follow-up question below it — the side preview portals its action chips into that question (via the slot).
    const previewBody = showArtifactPanel ? (
        <>
            {artifacts.length > 1 && (
                <ArtifactChips role='tablist' aria-label={T._('Artifacts', 'Admin')}>
                    {artifacts.map((item) => (
                        <ArtifactChip
                            key={item.kind}
                            type='button'
                            role='tab'
                            title={item.label}
                            aria-selected={item.kind === activeArtifact}
                            $selected={item.kind === activeArtifact}
                            onClick={() => setSelectedArtifact(item.kind)}
                        >
                            {item.label}
                        </ArtifactChip>
                    ))}
                </ArtifactChips>
            )}
            {isDraftRefreshRequired && (
                <Notice>
                    {T._('The preview is out of date. Refresh it before making another edit.', 'Admin')}
                    <AiOutlineButton type='button' $size='small' disabled={isInteractionBusy} onClick={() => void refreshPreview()}>
                        {T._('Refresh preview', 'Admin')}
                    </AiOutlineButton>
                </Notice>
            )}
            <InlinePreview>
                {isBuildingArtifact ? (
                    <AgentArtifactSkeleton kind={buildingArtifactKind} />
                ) : (
                    <AgentTaskPreview
                        draft={draft}
                        imageGenerations={imageGenerations}
                        artifact={activeArtifact}
                        revisionLabel={
                            displayedResultNumber(activeArtifact) != null ? `${T._('rev.', 'Admin')}${displayedResultNumber(activeArtifact)}` : undefined
                        }
                        disabled={isInteractionBusy || isDraftRefreshRequired}
                        actionsSlot={null}
                        entityLabel={activeArtifact ? ARTIFACT_INTRODUCTIONS[activeArtifact].entity : undefined}
                        entityStatus={activeArtifact ? artifactStatus(activeArtifact) : undefined}
                        entityExplanation={activeArtifact ? artifactIntroduction(activeArtifact) : undefined}
                        onRequestEdit={(message) => void send(message)}
                        onUpsertPrintArea={upsertPrintArea}
                        onReplaceSideImage={replaceSideImage}
                        onDeletePrintArea={deletePrintArea}
                        onRestorePrintArea={restorePrintArea}
                        onSetSideScale={setSideScale}
                    />
                )}
            </InlinePreview>
            {!isBuildingArtifact && actionQuestion}
        </>
    ) : null;

    // The preview and its follow-up question sit in the latest agent bubble (the drawer's only layout too).
    const inlinePreview = previewBody;

    // The conversation identity, history dropdown and new-conversation action. In full screen they portal into the
    // header (headerToolsSlot) to free vertical space; otherwise they sit at the top of the conversation column.
    // In a fresh, empty conversation the identity already reads "New conversation", so this start-over action is redundant.
    const startOverAction = hasConversation && (
        <ConversationActions>
            <StartOverButton type='button' onClick={restart} disabled={isInteractionBusy || isRestoring}>
                <Icon>
                    <RestartIcon />
                </Icon>
                {T._('New conversation', 'Admin')}
            </StartOverButton>
        </ConversationActions>
    );

    const conversationTools = (
        <ConversationTools $centered={isFullScreen}>
            <ConversationIdentity ref={conversationIdentityRef}>
                {isRenamingConversation ? (
                    <ConversationNameInput
                        autoFocus
                        value={conversationNameDraft}
                        maxLength={200}
                        onChange={(event) => setConversationNameDraft(event.currentTarget.value)}
                        onBlur={() => setIsRenamingConversation(false)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') void commitConversationRename();
                            if (event.key === 'Escape') setIsRenamingConversation(false);
                        }}
                    />
                ) : conversationId ? (
                    <ZTooltip position='bottom' title={T._('Rename', 'Admin')}>
                        <ConversationNameButton type='button' disabled={isInteractionBusy || isRestoring} onClick={beginConversationRename}>
                            {conversationName || T._('Untitled conversation', 'Admin')}
                        </ConversationNameButton>
                    </ZTooltip>
                ) : (
                    <ConversationState>{T._('New conversation', 'Admin')}</ConversationState>
                )}
                {!isRenamingConversation && (
                    <ZTooltip position='bottom' title={T._('Conversation history', 'Admin')}>
                        <ConversationToolButton
                            type='button'
                            aria-label={T._('Conversation history', 'Admin')}
                            aria-expanded={isHistoryOpen}
                            aria-controls={CONVERSATION_HISTORY_ID}
                            onClick={() => setIsHistoryOpen((open) => !open)}
                        >
                            <AccordionChevron $open={isHistoryOpen}>
                                <ExpandMoreIcon />
                            </AccordionChevron>
                        </ConversationToolButton>
                    </ZTooltip>
                )}
                {isHistoryOpen && conversationHistory}
            </ConversationIdentity>
            {startOverAction}
        </ConversationTools>
    );

    // The drawer shows neither the conversation name nor the history selector: only the way to start over.
    const drawerConversationTools = startOverAction && <ConversationTools>{startOverAction}</ConversationTools>;

    // One card per generated revision — each rev is one output. The full-screen rail and the drawer's Outputs tab both
    // use this; clicking a card scrolls to that result in the conversation. Different output types stay listed together
    // in order (nothing is removed), each labelled with its own type.
    // The plain entity name goes on the card; its type is a chip alongside the status and revision.
    const kindTypeLabels: Record<AgentArtifactKind, string> = {
        printingMethod: T._('Printing Method', 'Admin'),
        pricing: T._('Pricing Rules', 'Admin'),
        product: T._('Customizable Product', 'Admin')
    };
    const kindTypeLabel = (kind?: AgentArtifactKind) => (kind && kindTypeLabels[kind]) || T._('Output', 'Admin');
    const railBadgeLabel = (status: RailBadgeStatus) =>
        status === 'created' ? T._('Created', 'Admin') : status === 'unused' ? T._('Not used', 'Admin') : T._('Draft', 'Admin');
    const activeRevisionResultId = activeResultKey;
    const outputCards = () => (
        <>
            {revisionResults.map((result) => {
                const kind = resultKind(result);
                const status = resultStatuses.get(result.id) ?? 'draft';
                return (
                    <RailEntityCard key={result.id} type='button' $active={result.id === activeRevisionResultId} onClick={() => navigateToResult(result.id)}>
                        <RailEntityType>{kindTypeLabel(kind)}</RailEntityType>
                        <RailEntityLabel>{result.title || T._('Untitled', 'Admin')}</RailEntityLabel>
                        <RailEntityMeta>
                            <RailEntityBadge $status={status}>{railBadgeLabel(status)}</RailEntityBadge>
                            <RailRevPill>
                                {T._('rev.', 'Admin')}
                                {revByResultId.get(result.id)}
                            </RailRevPill>
                        </RailEntityMeta>
                    </RailEntityCard>
                );
            })}
            {/* The revision being generated is not a result yet: show it so the rail never looks empty mid-build. */}
            {isBuildingArtifact && buildingArtifactKind && (
                <RailEntityCard type='button' $active disabled>
                    <RailEntityLabel>{ARTIFACT_INTRODUCTIONS[buildingArtifactKind].entity}</RailEntityLabel>
                    <RailEntityMeta>
                        <RailEntityBadge $status='draft'>{T._('creating…', 'Admin')}</RailEntityBadge>
                    </RailEntityMeta>
                </RailEntityCard>
            )}
        </>
    );

    // The drawer always wraps the conversation in the tabs, so it is never remounted when the first preview arrives;
    // the tab strip and the Outputs tab only show once there are previews.
    const wrapInDrawerTabs = (conversationPanel: ReactNode) =>
        isFullScreen ? (
            conversationPanel
        ) : (
            <DrawerTabs $withRail={hasOutputs} selectedIndex={hasOutputs ? drawerTab : DRAWER_TAB_CONVERSATION} onSelect={(index) => setDrawerTab(index)}>
                {hasOutputs && (
                    <DrawerTabList>
                        <Tab>{T._('Conversation', 'Admin')}</Tab>
                        <Tab>{T._('Outputs', 'Admin')}</Tab>
                    </DrawerTabList>
                )}
                <ConversationTabPanel forceRender>{conversationPanel}</ConversationTabPanel>
                <OutputsTabPanel>
                    <OutputsList>{outputCards()}</OutputsList>
                </OutputsTabPanel>
            </DrawerTabs>
        );

    return (
        <Root>
            {/* The rail shows once the column is wide enough, in full screen and in a widened drawer alike. */}
            {hasOutputs && (
                <RailOverlay>
                    <RailEntities>
                        <RailEntitiesTitle>{T._('Outputs', 'Admin')}</RailEntitiesTitle>
                        {outputCards()}
                    </RailEntities>
                </RailOverlay>
            )}
            {wrapInDrawerTabs(
                <ConversationPanel $aiMode={isFullScreen} $centered={isFullScreen}>
                    {/* Full screen portals the conversation tools into the header bar (centered); the drawer keeps only "New conversation" at the top. */}
                    {isFullScreen ? headerToolsSlot && createPortal(conversationTools, headerToolsSlot) : drawerConversationTools}

                    <Transcript ref={transcriptRef} $centered={isFullScreen}>
                        {guide && (
                            <MessageGroup>
                                <AgentBubble $role='assistant'>
                                    <GuideHeader>
                                        <h3>{guide.title}</h3>
                                        <ZTooltip position='bottom' title={T._('Open in new tab', 'Admin')}>
                                            <GuideExternalLink
                                                href={`https://zakeke.zendesk.com/hc/articles/${guide.zendeskArticleId}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                aria-label={T._('Open in new tab', 'Admin')}
                                            >
                                                <Icon>
                                                    <OpenInNewIcon />
                                                </Icon>
                                            </GuideExternalLink>
                                        </ZTooltip>
                                    </GuideHeader>
                                    <HelpArticleBody articleId={guide.zendeskArticleId} body={guide.body} />
                                </AgentBubble>
                            </MessageGroup>
                        )}
                        {entries.length === 0 && !guide && !isRestoring && (
                            <Welcome>
                                <h3>{T._('What would you like to do?', 'Admin')}</h3>
                                <p>{T._('Describe what you need in your own words. The assistant will guide you through the available steps.', 'Admin')}</p>
                                <Suggestions>
                                    <AiOutlineButton type='button' $size='small' onClick={() => void send(T._('Show me what you can help me with.', 'Admin'))}>
                                        {T._('Explore capabilities', 'Admin')}
                                    </AiOutlineButton>
                                    <AiOutlineButton
                                        type='button'
                                        $size='small'
                                        onClick={() => void send(T._('Help me get started with a new task.', 'Admin'))}
                                    >
                                        {T._('Start a task', 'Admin')}
                                    </AiOutlineButton>
                                </Suggestions>
                            </Welcome>
                        )}

                        {entries.map((entry, entryIndex) => {
                            if (entry.role === 'notice') return <Notice key={entry.id}>{entry.message}</Notice>;
                            if (entry.role === 'draft-result') return null;
                            if (entry.role === 'merchant') {
                                return (
                                    <MessageGroup key={entry.id} $merchant>
                                        <MerchantBubble $role='user'>
                                            {entry.attachments.length > 0 && (
                                                <SentImages>
                                                    {entry.attachments.map((attachment) => (
                                                        <img key={attachment.id} src={attachment.previewUrl || attachment.url} alt={attachment.name} />
                                                    ))}
                                                </SentImages>
                                            )}
                                            {entry.text && <MessageText>{entry.text}</MessageText>}
                                            {entry.answers && (
                                                <AgentAnswerRecapCard
                                                    recap={entry.answers}
                                                    disabled={isInteractionBusy || hasLaterSubmission}
                                                    onEdit={
                                                        lastAgentTurn?.turnId &&
                                                        entries.some(
                                                            (item) => item.role === 'agent' && item.turn.questionnaire?.turnId === entry.answers!.turnId
                                                        )
                                                            ? () => {
                                                                  const source = entries.find(
                                                                      (item) =>
                                                                          item.role === 'agent' && item.turn.questionnaire?.turnId === entry.answers!.turnId
                                                                  );
                                                                  if (source?.role === 'agent' && source.turn.questionnaire) {
                                                                      setAnswerCorrection({
                                                                          questionnaire: source.turn.questionnaire,
                                                                          recap: entry.answers!,
                                                                          afterTurnId: lastAgentTurn.turnId!
                                                                      });
                                                                  }
                                                              }
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        </MerchantBubble>
                                    </MessageGroup>
                                );
                            }

                            const { turn } = entry;
                            // A completed task is confirmed in one message; what to do next is asked in a second one, with chips.
                            const appliedChanges = turnAppliedChanges(turn);
                            const rawText = turn.question || turn.message;
                            const text = appliedChanges && rawText ? withoutNextSteps(rawText) : rawText;
                            const nextSteps = turn.suggestedActions ?? [];
                            const isLatest = latestAgentEntry?.id === entry.id;
                            let nextEntryIndex = entryIndex + 1;
                            while (entries[nextEntryIndex]?.role === 'draft-result') nextEntryIndex++;
                            const publishedDrafts = entries.slice(entryIndex + 1, nextEntryIndex) as Extract<AgentChatEntry, { role: 'draft-result' }>[];
                            const visibleDrafts = publishedDrafts.filter((result) => visibleResultIds.has(result.id));
                            const approval =
                                (isLatest ? pendingApproval : null) ??
                                turn.approval ??
                                (draft.document?._meta?.approvalOffer && draft.document._meta.approvalOffer.plannedInTurn === turn.turnNumber
                                    ? draft.document._meta.approvalOffer
                                    : null);
                            // A later reply that applied changes means this approval went through.
                            const applyingTurn = approval
                                ? entries
                                      .slice(entryIndex + 1)
                                      .map((later) => (later.role === 'agent' ? later.turn : null))
                                      .find((later): later is AgentTurn => Boolean(later && turnAppliedChanges(later)))
                                : undefined;
                            const syntheticGesture: AgentGesture | null =
                                isLatest && !turn.gesture && !turn.questionnaire && pendingProposal?.entityReferenceKey
                                    ? { kind: 'area', sideRef: pendingProposal.entityReferenceKey, x1: 0, y1: 0, x2: 0, y2: 0 }
                                    : null;
                            // The area editor (gesture card) already shows the image to work on; the output preview must
                            // not open alongside it, or the two read as one confusing thing.
                            const areaEditorOpen = isLatest && Boolean(turn.gesture || syntheticGesture);
                            // While a plan waits for approval, the banner offers to apply it, so drop the redundant apply-question from the message.
                            const bodyText = approval && !applyingTurn && text ? withoutApprovalQuestion(text) : text;

                            return (
                                <React.Fragment key={entry.id}>
                                    <MessageGroup>
                                        <AgentBubble $role='assistant'>
                                            {!turn.supportRequest && bodyText && <AgentMessageText message={bodyText} />}
                                            {turn.supportRequest && <AiSupportCard embedded request={turn.supportRequest} />}
                                            {appliedChanges && <AgentEntityLinks turn={turn} draft={draft} />}
                                            {turn.resultRecap &&
                                                turn.resultRecap.state !== 'approval_required' &&
                                                followsWidgetAnswers(entries, entryIndex) && <AgentResultRecapCard recap={turn.resultRecap} />}
                                            {/* With a preview the generated images are already shown there, on their sides. */}
                                            {!hasPreview && turn.generatedImages && turn.generatedImages.length > 0 && (
                                                <GeneratedImages>
                                                    {turn.generatedImages.map((image) => (
                                                        <GeneratedImageCard key={`${image.taskId}:${image.sideRef}`}>
                                                            {image.url ? (
                                                                <a href={image.url} target='_blank' rel='noreferrer'>
                                                                    <img src={image.url} alt={image.prompt || image.sideRef} />
                                                                </a>
                                                            ) : (
                                                                <div />
                                                            )}
                                                            <GeneratedImageInfo>
                                                                <strong>{image.sideRef}</strong>
                                                                <span>
                                                                    {image.errorCode === 'addon_not_active'
                                                                        ? T._('Activate the required add-on to generate images.', 'Admin')
                                                                        : image.errorCode === 'no_credits'
                                                                          ? T._('Not enough AI credits', 'Admin')
                                                                          : image.errorMessage ||
                                                                            (image.state.toLowerCase() === 'completed'
                                                                                ? T._('Generated image', 'Admin')
                                                                                : image.state)}
                                                                </span>
                                                            </GeneratedImageInfo>
                                                        </GeneratedImageCard>
                                                    ))}
                                                </GeneratedImages>
                                            )}
                                            {turn.generatedImages?.some((image) => image.errorCode === 'no_credits') && (
                                                <NoCreditsCard>
                                                    <span>{T._('These images were not generated: the free AI credits were used up.', 'Admin')}</span>
                                                    <AiOutlineButton
                                                        type='button'
                                                        $size='small'
                                                        disabled={isInteractionBusy || isRetryPreflightInProgress}
                                                        onClick={() =>
                                                            retryFailedImages(turn.generatedImages!.filter((image) => image.errorCode === 'no_credits'))
                                                        }
                                                    >
                                                        {isRetryPreflightInProgress ? T._('Checking…', 'Admin') : T._('Retry', 'Admin')}
                                                    </AiOutlineButton>
                                                </NoCreditsCard>
                                            )}
                                            {turn.operationRequirements?.some(
                                                (requirement) => requirement.action === 'upgrade_plan' && !requirement.addonKey
                                            ) && (
                                                <AgentOperationActionCard
                                                    busy={isInteractionBusy}
                                                    onResume={() => send(T._('Check availability and resume the configuration for my review.', 'Admin'))}
                                                />
                                            )}
                                            {!turn.operationRequirements && turn.requiredPlanFeatures?.includes('advanced_pricing') && (
                                                <AgentPricingUpgradeCard
                                                    busy={isInteractionBusy}
                                                    onResume={() =>
                                                        send(T._('Check Advanced Pricing availability and resume the configuration for my review.', 'Admin'))
                                                    }
                                                />
                                            )}
                                            {(turn.operationRequirements
                                                ? [
                                                      ...new Set(
                                                          turn.operationRequirements
                                                              .filter(
                                                                  (requirement) =>
                                                                      requirement.addonKey &&
                                                                      ['activate_addon', 'upgrade_plan', 'purchase_credits'].includes(requirement.action ?? '')
                                                              )
                                                              .map((requirement) => requirement.addonKey!)
                                                      )
                                                  ]
                                                : requiredAddonsForTurn(turn)
                                            ).map((addonKey) => {
                                                const requirements = turn.operationRequirements?.filter((requirement) => requirement.addonKey === addonKey);
                                                const action = requirements?.some((requirement) => requirement.action === 'upgrade_plan')
                                                    ? 'upgrade_plan'
                                                    : requirements?.[0]?.action;
                                                const blockedImages = (turn.generatedImages ?? []).filter(
                                                    (image) => image.errorCode === 'addon_not_active' && (image.addonKey || 'AI_STUDIO') === addonKey
                                                );
                                                const retryImages = () => retryFailedImages(blockedImages);
                                                const resumeAddon = () =>
                                                    blockedImages.length > 0
                                                        ? retryImages()
                                                        : send(
                                                              turn.responseLanguage?.toLowerCase().startsWith('it')
                                                                  ? `Verifica se l'add-on ${addonKey} è disponibile e continua la configurazione per la mia revisione.`
                                                                  : `Check whether the ${addonKey} add-on is available and continue the configuration for my review.`
                                                          );
                                                return (
                                                    <AgentAddonActionCard
                                                        key={addonKey}
                                                        addonKey={addonKey}
                                                        action={action}
                                                        busy={isInteractionBusy || (addonKey === 'AI_STUDIO' && isRetryPreflightInProgress)}
                                                        onResume={resumeAddon}
                                                        onActivate={
                                                            addonKey === 'AI_STUDIO'
                                                                ? () => {
                                                                      if (blockedImages.length > 0) {
                                                                          retryImages();
                                                                          return;
                                                                      }
                                                                      setIsRetryPreflightInProgress(true);
                                                                      void runWithPreflight(
                                                                          async () => {
                                                                              await resumeAddon();
                                                                          },
                                                                          {
                                                                              onNoCredits: showSideImagesNoCreditsDialog
                                                                          }
                                                                      ).finally(() => setIsRetryPreflightInProgress(false));
                                                                  }
                                                                : undefined
                                                        }
                                                    />
                                                );
                                            })}
                                            {turn.outcomeCode && <Notice>{messageForOutcome(turn.outcomeCode)}</Notice>}
                                            {!appliedChanges && !turn.questionnaire && !approval && turn.options && turn.options.length > 0 && (
                                                <Options>
                                                    {turn.options.map((option) => (
                                                        <OptionButton
                                                            key={option}
                                                            type='button'
                                                            $size='small'
                                                            disabled={!isLatest || isInteractionBusy}
                                                            onClick={() => void send(option)}
                                                        >
                                                            {option}
                                                        </OptionButton>
                                                    ))}
                                                </Options>
                                            )}
                                            {/* No "created" card in the message: the created state stays below the output,
                                                in the green banner that appears there on Create, keeping things in order. */}
                                            {isLatest && (turn.gesture || syntheticGesture) && (
                                                <AgentGestureCard
                                                    request={(turn.gesture || syntheticGesture)!}
                                                    draft={draft}
                                                    defaultMessages={turn.defaultMessages}
                                                    disabled={isInteractionBusy}
                                                    onSubmit={submitGesture}
                                                    onConfirmArea={upsertPrintArea}
                                                    onDescribe={focusComposer}
                                                />
                                            )}
                                        </AgentBubble>
                                    </MessageGroup>
                                    {/* The preview is its own message, separate from the response. The latest result can
                                        ask for more edits: its chips portal into the question below, the older ones stay read-only.
                                        While the latest turn still has pending questions, its result is withheld to keep the flow linear. */}
                                    {visibleDrafts.length > 0 && conversationId && !(isLatest && wizardQuestionnaire) && (
                                        <>
                                            {visibleDrafts.map((publishedDraft) => (
                                                <MessageGroup
                                                    key={publishedDraft.id}
                                                    ref={(node) => {
                                                        if (node) resultRefs.current.set(publishedDraft.id, node);
                                                        else resultRefs.current.delete(publishedDraft.id);
                                                    }}
                                                >
                                                    <AgentDraftResultCard
                                                        conversationId={conversationId}
                                                        turnId={publishedDraft.turnId}
                                                        resultNumber={revByResultId.get(publishedDraft.id) ?? publishedDraft.resultNumber}
                                                        artifact={resultKind(publishedDraft)}
                                                        defaultExpanded={latestDraftResultEntry?.id === publishedDraft.id}
                                                        actionsSlot={null}
                                                        busy={isInteractionBusy}
                                                    />
                                                </MessageGroup>
                                            ))}
                                            {isLatest && !hasLaterSubmission && actionQuestion && <MessageGroup>{actionQuestion}</MessageGroup>}
                                        </>
                                    )}
                                    {/* The ready preview sits in the flow; the building skeleton is shown below the
                                        activity status instead (see after the transcript's activity indicator). While
                                        questions are still pending the output is withheld, so the flow stays linear. */}
                                    {isLatest &&
                                        !isBuildingArtifact &&
                                        !turn.hasDraftSnapshot &&
                                        turnAppliedChanges(turn) &&
                                        (resultOutputs.length === 0 ||
                                            (latestPublishedDraftRevision != null &&
                                                turn.draftRevisionNumber != null &&
                                                turn.draftRevisionNumber > latestPublishedDraftRevision)) &&
                                        !areaEditorOpen &&
                                        !wizardQuestionnaire &&
                                        inlinePreview && <MessageGroup ref={inlinePreviewRef}>{inlinePreview}</MessageGroup>}
                                    {/* The latest turn still needs its approval action when it publishes no new preview,
                                        including updates to an entity already in the catalogue. */}
                                    {approval &&
                                        !applyingTurn &&
                                        !hasLaterSubmission &&
                                        (!hasPreview || (isLatest && visibleDrafts.length === 0 && !turnAppliedChanges(turn))) && (
                                            <MessageGroup>
                                                <PlanBanner
                                                    title={T._('Approval required', 'Admin')}
                                                    message={approval.description || T._('Review the plan in the preview above.', 'Admin')}
                                                    reviseLabel={T._('Revise', 'Admin')}
                                                    confirmLabel={confirmApprovalLabel(
                                                        approval,
                                                        catalogueArtifactKinds.has(approvalArtifactKind(approval) ?? 'product')
                                                    )}
                                                    onRevise={revisePlan}
                                                    onConfirm={() => confirmPlan(approvalArtifactKind(approval) ?? 'unknown')}
                                                    disabled={isInteractionBusy}
                                                    showActions={isLatest}
                                                />
                                            </MessageGroup>
                                        )}
                                    {/* When a preview is ready with no plan to confirm, the "Ready for the catalogue"
                                        banner below the output already offers Request changes / Create product. */}
                                    {nextSteps.length > 0 && (
                                        <MessageGroup>
                                            <AgentBubble $role='assistant'>
                                                {turn.suggestedActionsPrompt && (
                                                    <AgentMarkdown>
                                                        <p>{turn.suggestedActionsPrompt}</p>
                                                    </AgentMarkdown>
                                                )}
                                                {/* Only the latest reply offers them: later on the conversation has already moved on. */}
                                                {isLatest && !hasLaterSubmission && (
                                                    <Options>
                                                        {nextSteps.map((step) => (
                                                            <NextStepChip
                                                                key={step.label}
                                                                type='button'
                                                                disabled={isInteractionBusy}
                                                                onClick={() =>
                                                                    step.id === 'create_product' ? createAnotherProduct(step.message) : void send(step.message)
                                                                }
                                                            >
                                                                {step.label}
                                                            </NextStepChip>
                                                        ))}
                                                    </Options>
                                                )}
                                            </AgentBubble>
                                        </MessageGroup>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {/* The waiting status is always the last message of the conversation, whatever is on screen above it. */}
                        {(isBusy || isRestoring) && (
                            <AgentActivityIndicator
                                request={currentRequest}
                                restoring={isRestoring}
                                refreshing={isRefreshingResult}
                                phrases={applyingPlanPhrases}
                            />
                        )}
                        {/* While an artifact is being built its skeleton appears under the activity status that says what
                            is happening ("Generating product images…"), not above it. */}
                        {isBuildingArtifact && inlinePreview && <MessageGroup ref={inlinePreviewRef}>{inlinePreview}</MessageGroup>}
                    </Transcript>

                    {error && (
                        <ErrorBanner
                            type='ERROR'
                            variant='slim'
                            content={error}
                            buttons={
                                canRetry
                                    ? [
                                          {
                                              label: hasPendingRequest ? T._('Check progress', 'Admin') : T._('Try again', 'Admin'),
                                              disabled: isBusy || isDraftBusy,
                                              onClick: () => void retry()
                                          }
                                      ]
                                    : undefined
                            }
                        />
                    )}
                    {!error && canRetry && (
                        <ErrorBanner
                            type='ERROR'
                            variant='slim'
                            content={T._('The request could not be completed. Would you like to try again?', 'Admin')}
                            buttons={[{ label: T._('Try again', 'Admin'), disabled: isBusy || isDraftBusy, onClick: () => void retry() }]}
                        />
                    )}
                    {composerError && <ErrorBanner type='ERROR' variant='slim' content={composerError} />}
                    {conversationId && wizardQuestionnaire && (
                        <AgentQuestionWizard
                            key={`${wizardQuestionnaire.turnId}-${answerCorrection?.afterTurnId ?? 'new'}`}
                            conversationId={conversationId}
                            questionnaire={wizardQuestionnaire}
                            disabled={isInteractionBusy || isRestoring}
                            initialAnswers={answerCorrection?.recap.answers}
                            correctionAfterTurnId={answerCorrection?.afterTurnId}
                            onCancel={answerCorrection ? () => setAnswerCorrection(null) : undefined}
                            onSubmit={async (submission) => {
                                const accepted = await sendAnswers(submission);
                                if (accepted) setAnswerCorrection(null);
                                return accepted;
                            }}
                        />
                    )}
                    <Composer>
                        {attachments.length > 0 && (
                            <Attachments>
                                {attachments.map((attachment) => (
                                    <Attachment key={attachment.id}>
                                        <img src={attachment.previewUrl} alt={attachment.name} />
                                        <AttachmentInfo>
                                            <span>{attachment.name}</span>
                                            <small>
                                                {attachment.status === 'uploading'
                                                    ? T._('Uploading…', 'Admin')
                                                    : attachment.status === 'failed'
                                                      ? attachment.error
                                                      : T._('Ready', 'Admin')}
                                            </small>
                                            {variantNames.length > 1 && (
                                                <AttachmentSelect
                                                    value={attachment.variant ?? ''}
                                                    onChange={(event) => updateAttachment(attachment.id, { variant: event.target.value || null })}
                                                >
                                                    <option value=''>{T._('Which variant?', 'Admin')}</option>
                                                    {variantNames.map((variant) => (
                                                        <option key={variant}>{variant}</option>
                                                    ))}
                                                </AttachmentSelect>
                                            )}
                                            {sideNames.length > 1 && (
                                                <AttachmentSelect
                                                    value={attachment.side ?? ''}
                                                    onChange={(event) => updateAttachment(attachment.id, { side: event.target.value || null })}
                                                >
                                                    <option value=''>{T._('Which side?', 'Admin')}</option>
                                                    {sideNames.map((side) => (
                                                        <option key={side}>{side}</option>
                                                    ))}
                                                </AttachmentSelect>
                                            )}
                                        </AttachmentInfo>
                                        <RemoveAttachment
                                            type='button'
                                            aria-label={T._('Remove attachment', 'Admin')}
                                            onClick={() => removeAttachment(attachment.id)}
                                        >
                                            ×
                                        </RemoveAttachment>
                                    </Attachment>
                                ))}
                            </Attachments>
                        )}
                        <ComposerDropZone
                            $dragging={isDraggingAttachments}
                            onDragEnter={handleComposerDragEnter}
                            onDragOver={handleComposerDragOver}
                            onDragLeave={handleComposerDragLeave}
                            onDrop={handleComposerDrop}
                            onPaste={handleComposerPaste}
                        >
                            {isDraggingAttachments && <ComposerDropHint>{T._('Drop images to attach', 'Admin')}</ComposerDropHint>}
                            <ComposerInputArea>
                                <HiddenFileInput ref={fileInputRef} type='file' accept='image/*' multiple onChange={attachFiles} />
                                <ComposerField $requesting={isRequestingChanges}>
                                    {isRequestingChanges && (
                                        <RequestingBadge>
                                            <Icon>
                                                <EditIcon />
                                            </Icon>
                                            <RequestingBadgeText>
                                                {T._('Requesting changes to', 'Admin')} {draft.document?.product?.name || T._('this product', 'Admin')}
                                                {draft.resultNumber != null ? ` · ${T._('rev.', 'Admin')}${displayedResultNumber(activeArtifact)}` : ''}
                                            </RequestingBadgeText>
                                        </RequestingBadge>
                                    )}
                                    {isRequestingChanges && (
                                        <RequestingExit
                                            type='button'
                                            aria-label={T._('Exit change request', 'Admin')}
                                            onClick={() => setIsRequestingChanges(false)}
                                        >
                                            <Icon>
                                                <CloseIcon />
                                            </Icon>
                                        </RequestingExit>
                                    )}
                                    <ChatInput
                                        ref={composerRef}
                                        value={message}
                                        disabled={isInteractionBusy}
                                        placeholder={
                                            isRequestingChanges
                                                ? T._('Describe what to change…', 'Admin')
                                                : hasConversation
                                                  ? T._('Write a message', 'Admin')
                                                  : T._('Ask the assistant or describe what you need…', 'Admin')
                                        }
                                        onChange={(event) => setMessage(event.target.value)}
                                        onKeyDown={onComposerKeyDown}
                                    />
                                </ComposerField>
                                <ComposerBottomRow>
                                    {/* Suggestions belong to the change-request state only: sending clears it, so they
                                        disappear and the prompt goes back to empty and small. */}
                                    {isRequestingChanges && composerQuickActions.length > 0 && (
                                        <PromptSuggestions>
                                            <PromptSuggestionsLabel>{T._('Suggestions:', 'Admin')}</PromptSuggestionsLabel>
                                            {composerQuickActions.map((action) => (
                                                <QuickActionChip
                                                    key={action.label}
                                                    type='button'
                                                    disabled={isInteractionBusy}
                                                    onClick={() => {
                                                        // Fill the prompt with the request; the merchant edits and sends it by hand.
                                                        setMessage(action.message);
                                                        focusComposer();
                                                    }}
                                                >
                                                    {action.label}
                                                </QuickActionChip>
                                            ))}
                                        </PromptSuggestions>
                                    )}
                                    <ComposerControls>
                                        <AttachButton
                                            type='button'
                                            $type='icon'
                                            $size='medium'
                                            disabled={isInteractionBusy}
                                            aria-label={T._('Attach images', 'Admin')}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <Icon>
                                                <AttachFileIcon />
                                            </Icon>
                                        </AttachButton>
                                        {isVoiceInputSupported && (
                                            <VoiceInputButton
                                                disabled={isInteractionBusy}
                                                isListening={isListening}
                                                onClick={() => {
                                                    setComposerError(null);
                                                    toggleListening();
                                                }}
                                            />
                                        )}
                                        {/* Use the send button to stop an active request, keeping the same style. */}
                                        <AiPromptSendButton
                                            type='button'
                                            $size={38}
                                            disabled={
                                                showStopButton
                                                    ? !canStopRequest
                                                    : isInteractionBusy || (!message.trim() && attachments.every((attachment) => attachment.status !== 'ready'))
                                            }
                                            aria-label={showStopButton ? T._('Stop request', 'Admin') : T._('Send message', 'Admin')}
                                            onClick={showStopButton ? () => void cancelRequest() : sendCurrentMessage}
                                        >
                                            <Icon>{showStopButton ? <StopIcon /> : <SendIcon />}</Icon>
                                        </AiPromptSendButton>
                                    </ComposerControls>
                                </ComposerBottomRow>
                            </ComposerInputArea>
                        </ComposerDropZone>
                    </Composer>
                </ConversationPanel>
            )}
        </Root>
    );
};
